import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Pagination,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Icon,
  DatePicker,
  ActionsMenu,
  ActionsMenuItem,
  ActionsMenuDivider,
  showToast,
} from '@repo/ui';
import { PageLayout } from '@xtnd/web/shared/layout';
import type { InvoiceQueryParams, TransformedInvoice } from '../types';
import {
  WebhookSimulationModal,
  BulkSimulateModal,
  DownloadMenu,
  BankTransactionsModal,
  ColumnVisibilityModal,
} from '../components';
import {
  useInvoices,
  useInvoiceStatusCounts,
  useResendEmailMutation,
  useBulkSendEmailsMutation,
  useRegeneratePdfMutation,
  useBulkRegeneratePdfMutation,
  useBulkRefreshFromClioMutation,
  useBulkSimulateMutation,
  useUsersQuery,
  useFilteredUsersQuery,
} from '../queries';
import { numberUtil, dateUtil } from '@xtnd/web/shared/utils';
import { api } from '@xtnd/web/shared/api';
import { TrustRequestsTransactionsView } from '@xtnd/web/features/trust-requests/components';

export function JuniperTransactionsPage() {
  // Tab state for Evergreen vs Trust Requests
  const [activeTab, setActiveTab] = useState<'evergreen' | 'trust-requests'>('evergreen');

  // Helper functions for status formatting and date checking
  const formatRawStatus = (state: string | null): string => {
    if (!state) return 'Draft';

    const statusMap: Record<string, string> = {
      not_created: 'Not Created',
      not_required: 'Not Required',
      draft: 'Draft',
      awaiting_approval: 'Awaiting Approval',
      awaiting_payment: 'Awaiting Payment',
      partial_payment: 'Partial Payment',
      pending_payment: 'Pending Payment',
      paid: 'Paid',
      void: 'Void',
      voided: 'Voided',
      deleted: 'Deleted',
      'n/a': 'Not Applicable',
    };

    return statusMap[state] || state;
  };

  // Format date for responsive display (date on top, time below on small screens)
  const formatResponsiveDateTime = (
    dateString: string | null | undefined
  ): { date: string; time: string } => {
    if (!dateString) return { date: '', time: '' };

    // Handle invalid date strings
    const date = new Date(dateString);
    if (isNaN(date.getTime()) || dateString === 'Invalid Date') {
      return { date: '', time: '' };
    }

    // Use locale-aware date formatting
    const dateFormatted = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });

    const timeFormatted = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    return { date: dateFormatted, time: timeFormatted };
  };

  // Juniper Status badge colors (consistent with Invoice Detail page)
  const getJuniperStatusColor = (status: string): string => {
    const baseClass = 'inline-flex px-2 py-1 text-xs font-semibold rounded-full';

    switch (status.toLowerCase()) {
      case 'completed':
      case 'email sent':
      case 'archived':
        return `${baseClass} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'processing':
      case 'pdf ready':
        return `${baseClass} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'trust pending approval':
      case 'trust request approved':
      case 'email held':
        return `${baseClass} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'failed':
      case 'trust request deleted':
      case 'trust request voided':
      case 'operational bill voided':
      case 'operational bill deleted':
        return `${baseClass} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`;
    }
  };

  // TRR Status badge colors (consistent with Invoice Detail page)
  const getTrustRequestStatusColor = (status: string): string => {
    const baseClass = 'inline-flex px-2 py-1 text-xs font-semibold rounded-full';

    switch (status.toLowerCase()) {
      case 'paid':
      case 'not required':
        return `${baseClass} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'partial payment':
        return `${baseClass} bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-200`;
      case 'pending_payment':
        return `${baseClass} bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200`;
      case 'approved':
      case 'sent':
        return `${baseClass} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'awaiting payment':
        return `${baseClass} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'draft':
      case 'awaiting approval':
        return `${baseClass} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'not created':
        return `${baseClass} bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300`;
      case 'cancelled':
      case 'void':
      case 'voided':
      case 'deleted':
        return `${baseClass} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`;
    }
  };

  // Filter button text colors (matching status badge colors)
  const getFilterButtonTextColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'text-green-900 dark:text-green-900';
      case 'partial payment':
        return 'text-blue-900 dark:text-blue-900';
      case 'pending_payment':
        return 'text-lime-900 dark:text-lime-900';
      case 'awaiting_payment':
        return 'text-yellow-900 dark:text-yellow-900';
      case 'awaiting_approval':
      case 'draft':
        return 'text-yellow-900 dark:text-yellow-900';
      case 'not_created':
        return 'text-gray-700 dark:text-gray-900';
      case 'void':
      case 'deleted':
        return 'text-red-900 dark:text-red-900';
      default:
        return 'text-gray-900 dark:text-gray-900';
    }
  };

  // Status count text colors (matching the actual status colors, not section colors)
  const getStatusCountTextColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'paid':
      case 'completed':
      case 'email sent':
      case 'archived':
        return 'text-green-700 dark:text-green-300';
      case 'partial payment':
        return 'text-blue-700 dark:text-blue-300';
      case 'pending_payment':
        return 'text-lime-700 dark:text-lime-300';
      case 'processing':
      case 'pdf ready':
      case 'approved':
      case 'sent':
        return 'text-blue-700 dark:text-blue-300';
      case 'awaiting_payment':
        return 'text-yellow-700 dark:text-yellow-300';
      case 'awaiting_approval':
      case 'draft':
      case 'trust pending approval':
      case 'trust request approved':
      case 'email held':
        return 'text-yellow-700 dark:text-yellow-300';
      case 'not_created':
        return 'text-gray-700 dark:text-gray-300';
      case 'void':
      case 'deleted':
      case 'failed':
      case 'cancelled':
      case 'voided':
        return 'text-red-700 dark:text-red-300';
      default:
        return 'text-gray-700 dark:text-gray-300';
    }
  };

  // Helper functions for collapsed filter summaries
  const getSelectedFiltersSummary = (): string => {
    const operationalCount = selectedOperationalStatuses.length;
    const trustRequestCount = selectedTrustRequestStatuses.length;
    const userCount = selectedUserIds.length;
    const totalSelected = operationalCount + trustRequestCount + userCount;

    if (totalSelected === 0) return 'No filters selected';

    const parts = [];
    if (operationalCount > 0) parts.push(`${operationalCount} operational`);
    if (trustRequestCount > 0) parts.push(`${trustRequestCount} trust request`);
    if (userCount > 0) parts.push(`${userCount} user${userCount === 1 ? '' : 's'}`);

    return `${parts.join(', ')} filter${totalSelected === 1 ? '' : 's'} selected`;
  };

  const getSelectedCardsSummary = (): string => {
    if (!activeStatusFilter) return 'No card selected';

    const filterName = getFilterDisplayName(activeStatusFilter);
    let count = 0;

    switch (activeStatusFilter) {
      case 'alert':
        count = counts.alert;
        break;
      case 'deletedBills':
        // Calculate deleted/voided bills count
        count = invoices.filter((invoice: any) => {
          const operationalBillStatus = invoice.operationalBill?.state?.toLowerCase() || '';
          const trustRequestBillStatus = invoice.trustRequest?.state?.toLowerCase() || '';
          return (
            operationalBillStatus === 'deleted' ||
            operationalBillStatus === 'void' ||
            operationalBillStatus === 'voided' ||
            trustRequestBillStatus === 'deleted' ||
            trustRequestBillStatus === 'void' ||
            trustRequestBillStatus === 'voided'
          );
        }).length;
        break;
      case 'operationalPreApproval':
        count = counts.operationalPreApproval || 0;
        break;
      case 'trrCreated':
        count = counts.trrCreated;
        break;
      case 'pending':
        count = counts.pending;
        break;
      case 'sent':
        count = counts.sent;
        break;
      default:
        count = 0;
    }

    return `${filterName}: ${count} transaction${count !== 1 ? 's' : ''}`;
  };

  const getDateRangeSummary = (): string => {
    if (!startDate || !endDate) return 'All dates';
    return `${dateUtil.formatDate(new Date(startDate), dateUtil.DateFormat.US)} - ${dateUtil.formatDate(new Date(endDate), dateUtil.DateFormat.US)}`;
  };

  // Helper function to check if transaction has valid recipients
  const hasValidRecipients = (invoice: TransformedInvoice): boolean => {
    if (!invoice.billRecipientEmails || invoice.billRecipientEmails.length === 0) {
      return false;
    }

    // Handle both formats: array of strings or array of objects
    const validRecipients = invoice.billRecipientEmails.filter(recipient => {
      // If recipient is a string, it's a valid email address
      if (typeof recipient === 'string') {
        return recipient && (recipient as string).trim() !== '';
      }

      // If recipient is an object, check for is_bill_recipient=true AND has non-empty primary_email_address
      if (typeof recipient === 'object' && recipient !== null) {
        return (
          recipient.is_bill_recipient &&
          recipient.primary_email_address &&
          recipient.primary_email_address.trim() !== ''
        );
      }

      return false;
    });

    return validRecipients.length > 0;
  };

  // Helper function to get warning messages for a transaction
  const getTransactionWarnings = (invoice: TransformedInvoice): string[] => {
    const warnings: string[] = [];

    // Check for missing recipient emails
    if (!hasValidRecipients(invoice)) {
      if (!invoice.billRecipientEmails || invoice.billRecipientEmails.length === 0) {
        warnings.push('No bill recipients found');
      } else {
        warnings.push('No valid email addresses for bill recipients');
      }
    }

    // Check for operational bill voided but trust request not deleted/voided
    const operationalBillStatus = invoice.operationalBill?.state?.toLowerCase() || '';
    const trustRequestBillStatus = invoice.trustRequest?.state?.toLowerCase() || '';
    if (
      operationalBillStatus === 'void' &&
      trustRequestBillStatus !== 'deleted' &&
      trustRequestBillStatus !== 'void' &&
      trustRequestBillStatus !== 'voided'
    ) {
      warnings.push('The Deleting or Voiding the trust request should be evaluated');
    }

    return warnings;
  };

  // Email eligibility helper functions
  const isEmailEligible = (invoice: TransformedInvoice): boolean => {
    // Allow both 'Email Held' and 'Completed' transactions to be selectable
    // - Email Held: Initial send
    // - Completed: Resend email
    const isValidStatus =
      invoice.juniperStatus === 'Email Held' || invoice.juniperStatus === 'Completed';

    if (!isValidStatus) {
      return false;
    }

    // Must have valid recipients to be eligible for email sending
    return hasValidRecipients(invoice);
  };

  // Check if invoice is eligible for resend/release (must have trust request state awaiting_payment, paid, or not_required when Email Held)
  const isResendEligible = (invoice: TransformedInvoice): boolean => {
    const trustRequestState = invoice.trustRequest?.state?.toLowerCase();
    const isEmailHeld = invoice.juniperStatus?.toLowerCase() === 'email held';

    return (
      trustRequestState === 'awaiting_payment' ||
      trustRequestState === 'paid' ||
      (trustRequestState === 'not_required' && isEmailHeld)
    );
  };

  // Check if invoice is eligible for PDF regeneration (must have trust request state Awaiting_payment, paid, or not_required)
  const isPdfRegenerationEligible = (invoice: TransformedInvoice): boolean => {
    const trustRequestState = invoice.trustRequest?.state?.toLowerCase();
    return (
      trustRequestState === 'awaiting_payment' ||
      trustRequestState === 'paid' ||
      trustRequestState === 'not_required'
    );
  };

  // Helper function to get default date range (current week)
  const getDefaultDateRange = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Get Sunday of current week
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (6 - today.getDay())); // Get Saturday of current week

    return {
      startDate: startOfWeek.toISOString().split('T')[0],
      endDate: endOfWeek.toISOString().split('T')[0],
    };
  };

  // URL search params for date persistence
  const [searchParams, setSearchParams] = useSearchParams();

  // Date range state for the date picker - initialized from URL params or defaults
  const [startDate, setStartDate] = useState(() => {
    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');

    if (urlStartDate && urlEndDate) {
      // Validate that the dates are reasonable (not too far in the past or future)
      const start = new Date(urlStartDate);
      const end = new Date(urlEndDate);
      const now = new Date();

      // Check if dates are within a reasonable range (last 2 years to next year)
      const twoYearsAgo = new Date(now.getFullYear() - 2, 0, 1);
      const nextYear = new Date(now.getFullYear() + 1, 11, 31);

      if (start >= twoYearsAgo && end <= nextYear && start <= end) {
        return urlStartDate;
      }
    }

    // Fall back to default date range
    return getDefaultDateRange().startDate;
  });

  const [endDate, setEndDate] = useState(() => {
    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');

    if (urlStartDate && urlEndDate) {
      // Validate that the dates are reasonable (not too far in the past or future)
      const start = new Date(urlStartDate);
      const end = new Date(urlEndDate);
      const now = new Date();

      // Check if dates are within a reasonable range (last 2 years to next year)
      const twoYearsAgo = new Date(now.getFullYear() - 2, 0, 1);
      const nextYear = new Date(now.getFullYear() + 1, 11, 31);

      if (start >= twoYearsAgo && end <= nextYear && start <= end) {
        return urlEndDate;
      }
    }

    // Fall back to default date range
    return getDefaultDateRange().endDate;
  });

  // Wrapper function to update both state and URL parameters
  const updateDateRange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('startDate', newStartDate);
    newSearchParams.set('endDate', newEndDate);
    setSearchParams(newSearchParams);
  };

  // Filter state
  const [activeStatusFilter, setActiveStatusFilter] = useState<string | null>(null);
  const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);

  // Completed sub-filter state - initialized from URL params
  const [selectedOperationalStatuses, setSelectedOperationalStatuses] = useState<string[]>(() => {
    const urlParams = searchParams.get('operationalStatuses');
    return urlParams ? urlParams.split(',').filter(Boolean) : [];
  });
  const [selectedTrustRequestStatuses, setSelectedTrustRequestStatuses] = useState<string[]>(() => {
    const urlParams = searchParams.get('trustRequestStatuses');
    return urlParams ? urlParams.split(',').filter(Boolean) : [];
  });

  // User filter state
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(() => {
    const urlParams = searchParams.get('userIds');
    return urlParams ? urlParams.split(',').filter(Boolean) : [];
  });

  // Collapsible state for filter sections
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [isSummaryCollapsed, setIsSummaryCollapsed] = useState(false);
  const [isCardsCollapsed, setIsCardsCollapsed] = useState(false);

  // Pagination state - cursor-based pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const saved = sessionStorage.getItem('juniper-transactions-page-size');
    if (saved) {
      try {
        const parsed = parseInt(saved, 10);
        return isNaN(parsed) ? 100 : parsed;
      } catch (e) {
        console.warn('Failed to parse saved page size, using default');
      }
    }
    return 100; // Default page size
  });
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(undefined);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);

  // Bulk selection state
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('juniper-transactions-columns');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Ensure backward compatibility - add new balance columns if missing
        return {
          ...parsed,
          operationalBillBalance: parsed.operationalBillBalance ?? false,
          trustRequestBillBalance: parsed.trustRequestBillBalance ?? false,
        };
      } catch (e) {
        console.warn('Failed to parse saved column visibility, using defaults');
      }
    }
    // Default column visibility - only toggleable columns can be hidden
    return {
      clientName: true,
      createdAt: true,
      updatedAt: true,
      paymentLink: true,
      operationalBillTotal: true,
      operationalBillPaid: true,
      operationalBillBalance: false,
      operationalBillIssuedAt: true,
      operationalBillDueAt: true,
      trustRequestBillTotal: true,
      trustRequestBillPaid: true,
      trustRequestBillBalance: false,
      trustRequestBillPending: true,
      trustRequestBillIssuedAt: true,
      trustRequestBillDueAt: true,
      trustRequestCalculation: true,
    };
  });

  // Save column visibility to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem('juniper-transactions-columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Save page size to sessionStorage whenever it changes
  React.useEffect(() => {
    sessionStorage.setItem('juniper-transactions-page-size', pageSize.toString());
  }, [pageSize]);

  // Column visibility modal state
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);

  // Action menu states
  const [downloadMenuState, setDownloadMenuState] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
    transaction: any;
  }>({ isOpen: false, position: { x: 0, y: 0 }, transaction: null });
  const [reprocessing, setReprocessing] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<any | null>(null);

  // PDF regeneration confirmation state
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);
  const [regenerateConfirmType, setRegenerateConfirmType] = useState<'single' | 'bulk'>('single');
  const [regenerateConfirmTarget, setRegenerateConfirmTarget] = useState<any | null>(null);
  const [regenerateConfirmBulkIds, setRegenerateConfirmBulkIds] = useState<string[]>([]);
  const [overrideActive, setOverrideActive] = useState(false);
  const [toRecipients, setToRecipients] = useState<string[]>([]);
  const [bccRecipients, setBccRecipients] = useState<string[]>([]);
  const [actualRecipients, setActualRecipients] = useState<string[]>([]);
  const [actionsMenuOpen, setActionsMenuOpen] = useState<string | null>(null); // Track which row's menu is open
  const [menuTriggerElement, setMenuTriggerElement] = useState<HTMLElement | null>(null); // Track trigger element for menu positioning
  const [bankTransactionsModalOpen, setBankTransactionsModalOpen] = useState(false);
  const [selectedTransactionForBankModal, setSelectedTransactionForBankModal] =
    useState<TransformedInvoice | null>(null);
  const [bulkSimulateModalOpen, setBulkSimulateModalOpen] = useState(false);

  // Reset pagination when filters change
  const resetPagination = () => {
    setCurrentPage(1);
    setCurrentCursor(undefined);
    setHasNextPage(false);
    setHasPreviousPage(false);
    setTotalRecords(0);
    setCursorHistory([]);
  };

  // Helper function to update URL params for sub-filters
  const updateSubFilterUrlParams = (
    operationalStatuses: string[],
    trustRequestStatuses: string[],
    userIds?: string[]
  ) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (operationalStatuses.length > 0) {
      newSearchParams.set('operationalStatuses', operationalStatuses.join(','));
    } else {
      newSearchParams.delete('operationalStatuses');
    }
    if (trustRequestStatuses.length > 0) {
      newSearchParams.set('trustRequestStatuses', trustRequestStatuses.join(','));
    } else {
      newSearchParams.delete('trustRequestStatuses');
    }
    if (userIds && userIds.length > 0) {
      newSearchParams.set('userIds', userIds.join(','));
    } else {
      newSearchParams.delete('userIds');
    }
    setSearchParams(newSearchParams);
  };

  // Handle status card clicks for filtering
  const handleStatusCardClick = (filterType: string) => {
    console.log('🎯 Status card clicked:', filterType);
    if (activeStatusFilter === filterType) {
      setActiveStatusFilter(null);
      // Clear sub-filters when deselecting completed filter
      setSelectedOperationalStatuses([]);
      setSelectedTrustRequestStatuses([]);
      setSelectedUserIds([]);
      // Clear URL params
      updateSubFilterUrlParams([], [], []);
      resetPagination();
    } else {
      setActiveStatusFilter(filterType);
      // Clear sub-filters when switching to different filter
      if (filterType !== 'sent') {
        setSelectedOperationalStatuses([]);
        setSelectedTrustRequestStatuses([]);
        setSelectedUserIds([]);
        updateSubFilterUrlParams([], [], []);
      }
    }
    resetPagination();
  };

  // Handle sub-filter selection for operational status
  const handleOperationalStatusToggle = (status: string) => {
    const newStatuses = selectedOperationalStatuses.includes(status)
      ? selectedOperationalStatuses.filter(s => s !== status)
      : [...selectedOperationalStatuses, status];
    setSelectedOperationalStatuses(newStatuses);
    updateSubFilterUrlParams(newStatuses, selectedTrustRequestStatuses);
    resetPagination();
  };

  // Handle sub-filter selection for trust request status
  const handleTrustRequestStatusToggle = (status: string) => {
    const newStatuses = selectedTrustRequestStatuses.includes(status)
      ? selectedTrustRequestStatuses.filter(s => s !== status)
      : [...selectedTrustRequestStatuses, status];
    setSelectedTrustRequestStatuses(newStatuses);
    updateSubFilterUrlParams(selectedOperationalStatuses, newStatuses, selectedUserIds);
    resetPagination();
  };

  // Handle user filter selection
  const handleUserToggle = (userId: string) => {
    const newUserIds = selectedUserIds.includes(userId)
      ? selectedUserIds.filter(id => id !== userId)
      : [...selectedUserIds, userId];
    setSelectedUserIds(newUserIds);
    updateSubFilterUrlParams(selectedOperationalStatuses, selectedTrustRequestStatuses, newUserIds);
    resetPagination();
  };

  // Pagination handlers - cursor-based
  const handlePageChange = (page: number) => {
    if (page > currentPage) {
      handleNextPage();
    } else if (page < currentPage) {
      handlePreviousPage();
    }
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    resetPagination();
  };

  const handleNextPage = () => {
    if (hasNextPage && invoicesResponse?.nextCursor) {
      const cursorToStore = currentCursor || 'FIRST_PAGE';
      setCursorHistory(prev => [...prev, cursorToStore]);

      setCurrentPage(prev => prev + 1);
      setCurrentCursor(invoicesResponse.nextCursor);
      setHasPreviousPage(true);

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage && cursorHistory.length > 0) {
      const newHistory = [...cursorHistory];
      const previousCursor = newHistory.pop();

      setCursorHistory(newHistory);
      setCurrentPage(prev => prev - 1);

      if (previousCursor === 'FIRST_PAGE') {
        setCurrentCursor(undefined);
      } else {
        setCurrentCursor(previousCursor);
      }

      setHasPreviousPage(newHistory.length > 0);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Get display name for filter type
  const getFilterDisplayName = (filterType: string): string => {
    const displayNames: Record<string, string> = {
      alert: 'Alert (Stuck/Failed)',
      operationalPreApproval: 'Operational Pre Approval',
      pending: 'Juniper Email on Hold',
      trrCreated: 'Pending TRR Approval',
      sent: 'Juniper Completed',
      deletedBills: 'Deleted & Voided Bills',
    };
    return displayNames[filterType] || filterType;
  };

  // Build query parameters for API
  const queryParams = useMemo((): InvoiceQueryParams => {
    const params: InvoiceQueryParams = {
      limit: pageSize,
      dateFrom: startDate,
      dateTo: endDate,
      uniqueByMatter: false, // CRITICAL: Show ALL transactions, not just unique by matter
      createdByUserIds: selectedUserIds.length > 0 ? selectedUserIds : undefined,
    };

    if (currentCursor) {
      params.cursor = currentCursor;
    }

    console.log('🔧 Juniper Transactions Query Params:', {
      limit: params.limit,
      pageSize: pageSize,
      cursor: params.cursor,
      page: currentPage,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      uniqueByMatter: params.uniqueByMatter,
      hasActiveFilter: !!activeStatusFilter,
      activeFilter: activeStatusFilter,
    });

    return params;
  }, [
    currentCursor,
    pageSize,
    currentPage,
    startDate,
    endDate,
    activeStatusFilter,
    selectedUserIds,
  ]);

  // Fetch invoices and status counts using existing hooks
  const {
    data: invoicesResponse,
    isLoading: isLoadingInvoices,
    isFetching: isFetchingInvoices,
    error: invoicesError,
    refetch: refetchInvoices,
  } = useInvoices(queryParams);

  const {
    data: statusCounts,
    isLoading: isLoadingCounts,
    refetch: refetchStatusCounts,
  } = useInvoiceStatusCounts(queryParams);

  // Fetch user names for filtering (filtered to only users with transactions)
  const {
    data: userNames,
    isLoading: isLoadingUsers,
    error: usersError,
  } = useFilteredUsersQuery(invoicesResponse?.data || []);

  // Extract invoices from response
  const invoices = useMemo(
    () => (Array.isArray(invoicesResponse?.data) ? invoicesResponse.data : []),
    [invoicesResponse?.data]
  );

  // Debug logging
  React.useEffect(() => {
    console.log('📊 Juniper Transactions Debug:', {
      totalInvoices: invoices.length,
      activeStatusFilter,
      dateRange: { startDate, endDate },
      pageSize,
      hasActiveFilter: !!activeStatusFilter,
    });
  }, [invoices.length, activeStatusFilter, startDate, endDate, pageSize]);

  const counts = statusCounts || {
    pending: 0,
    alert: 0,
    trrCreated: 0,
    sent: 0,
    paid: 0,
    operationalPreApproval: 0,
  };

  // Update pagination state from response
  React.useEffect(() => {
    if (invoicesResponse) {
      console.log('📊 Invoice Response Debug:', {
        dataLength: invoicesResponse.data?.length || 0,
        hasMore: invoicesResponse.hasMore,
        nextCursor: invoicesResponse.nextCursor,
        metadata: invoicesResponse.metadata,
        totalFetched: invoicesResponse.metadata?.totalFetched,
        limit: invoicesResponse.limit,
        currentPage,
        cursorHistoryLength: cursorHistory.length,
      });

      // Update hasNextPage based on API response
      const hasMore = Boolean(invoicesResponse.hasMore && invoicesResponse.nextCursor);
      console.log('🔄 Setting hasNextPage to:', hasMore, {
        hasMoreFromAPI: invoicesResponse.hasMore,
        hasNextCursor: !!invoicesResponse.nextCursor,
        nextCursorValue: invoicesResponse.nextCursor,
      });
      setHasNextPage(hasMore);
      setTotalRecords(0);
    }
  }, [invoicesResponse]);

  // Helper function to check if a transaction is stuck (for alert filtering)
  const isTransactionStuck = (invoice: TransformedInvoice): boolean => {
    if (!invoice || !invoice.juniperStatus || !invoice.updatedAt) {
      return false;
    }

    const status = invoice.juniperStatus;
    const updatedAt = new Date(invoice.updatedAt);
    const now = new Date();
    const fourHoursAgo = new Date(now.getTime() - 4 * 60 * 60 * 1000);

    if (isNaN(updatedAt.getTime())) {
      return false;
    }

    return (
      (status === 'Processing' && updatedAt < fourHoursAgo) ||
      (status === 'Trust Request Approved' && updatedAt < fourHoursAgo) ||
      (status === 'PDF Ready' && updatedAt < fourHoursAgo)
    );
  };

  // Helper function to get bill run period from transaction date
  const getBillRunPeriod = (transactionDate: string) => {
    // Handle invalid date strings
    if (
      !transactionDate ||
      transactionDate === 'Invalid Date' ||
      transactionDate === 'null' ||
      transactionDate === 'undefined' ||
      transactionDate.trim() === ''
    ) {
      return 'No Date';
    }

    const date = new Date(transactionDate);
    if (isNaN(date.getTime())) {
      return 'No Date';
    }

    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Validate dayOfWeek
    if (isNaN(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 6) {
      return 'No Date';
    }

    // Calculate Monday of the current week for all transactions
    // This ensures consistent "Week of..." grouping as requested by the user
    const mondayOfCurrentWeek = new Date(date);
    const daysToSubtractForCurrent = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    mondayOfCurrentWeek.setDate(date.getDate() - daysToSubtractForCurrent);

    // Validate the calculated mondayOfCurrentWeek date
    if (isNaN(mondayOfCurrentWeek.getTime())) {
      return 'No Date';
    }

    return `Week of ${mondayOfCurrentWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  // Group transactions by bill run periods
  const groupedInvoices = useMemo(() => {
    if (!invoices || !Array.isArray(invoices)) {
      return [];
    }

    let filtered = [...invoices];

    // Status card filtering - always apply filtering
    filtered = filtered.filter(invoice => {
      if (!invoice || !invoice.juniperStatus) return false;

      const status = invoice.juniperStatus;
      const isStuck = isTransactionStuck(invoice);

      // Declare variables before using them
      const operationalBillStatus = invoice.operationalBill?.state?.toLowerCase() || '';
      const trustRequestBillStatus = invoice.trustRequest?.state?.toLowerCase() || '';

      // Check if transaction matches alert conditions (matches API count logic)
      const isAlert =
        status === 'Failed' ||
        // Operational bill is voided but trust request is not deleted/voided
        (operationalBillStatus === 'void' &&
          trustRequestBillStatus !== 'deleted' &&
          trustRequestBillStatus !== 'void' &&
          trustRequestBillStatus !== 'voided');

      const isCompleted = status === 'Completed';

      const isDeleted =
        // Check bill states (operational and trust request bills)
        operationalBillStatus === 'deleted' ||
        operationalBillStatus === 'void' ||
        operationalBillStatus === 'voided' ||
        trustRequestBillStatus === 'deleted' ||
        trustRequestBillStatus === 'void' ||
        trustRequestBillStatus === 'voided' ||
        // Check Juniper transaction status (for voided/deleted transactions)
        status === 'Trust Request Deleted' ||
        status === 'Trust Request Voided' ||
        status === 'Operational Bill Voided' ||
        status === 'Operational Bill Deleted';

      // Determine if transaction matches main filter
      let matchesMainFilter = true; // Default to true (show all)

      if (activeStatusFilter) {
        // If main filter is active, apply it
        switch (activeStatusFilter) {
          case 'alert':
            matchesMainFilter = isAlert;
            break;
          case 'operationalPreApproval':
            matchesMainFilter =
              operationalBillStatus === 'draft' || operationalBillStatus === 'awaiting_approval';
            break;
          case 'pending':
            matchesMainFilter = status === 'Email Held';
            break;
          case 'trrCreated':
            matchesMainFilter = status === 'Trust Pending Approval';
            break;
          case 'sent':
            matchesMainFilter = isCompleted;
            break;
          case 'deletedBills':
            matchesMainFilter = isDeleted;
            break;
          default:
            matchesMainFilter = true;
            break;
        }
      } else {
        // If no main filter, hide unwanted categories (alert, completed, deleted)
        matchesMainFilter = !isAlert && !isCompleted && !isDeleted;
      }

      // If transaction doesn't match the main filter, exclude it
      if (!matchesMainFilter) {
        return false;
      }

      // Apply sub-filters (operational and trust request status) - always applied regardless of main filter
      // If any sub-filters are selected, the transaction must match ALL selected sub-filters
      // If no sub-filters are selected, all transactions pass this check
      let subFilterMatch = true;

      // Operational status filter: if selected, must match
      if (selectedOperationalStatuses.length > 0) {
        subFilterMatch =
          subFilterMatch && selectedOperationalStatuses.includes(operationalBillStatus);
      }

      // Trust request status filter: if selected, must match
      if (selectedTrustRequestStatuses.length > 0) {
        subFilterMatch =
          subFilterMatch && selectedTrustRequestStatuses.includes(trustRequestBillStatus);
      }

      return subFilterMatch;
    });

    console.log(
      `📊 Filtered to ${filtered.length} transactions (main filter: ${activeStatusFilter || 'none'}, operational: [${selectedOperationalStatuses.join(', ')}], trust request: [${selectedTrustRequestStatuses.join(', ')}])`
    );

    // Group by bill run periods
    const groups: Array<{ period: string; transactions: any[] }> = [];
    let currentPeriod: string | null = null;
    let currentGroup: any[] = [];

    // Sort by bill created date (newest first)
    filtered.sort((a, b) => {
      const aDate = new Date(a.createdAt || 0).getTime();
      const bDate = new Date(b.createdAt || 0).getTime();
      return bDate - aDate;
    });

    filtered.forEach(transaction => {
      // Use the transaction's created_at date for grouping
      const billCreatedAt = transaction.createdAt || '';
      const period = getBillRunPeriod(billCreatedAt);

      if (period !== currentPeriod) {
        // Start new group
        if (currentGroup.length > 0) {
          groups.push({ period: currentPeriod || 'No Period', transactions: currentGroup });
        }
        currentPeriod = period;
        currentGroup = [transaction];
      } else {
        currentGroup.push(transaction);
      }
    });

    // Add final group
    if (currentGroup.length > 0) {
      groups.push({ period: currentPeriod || 'No Period', transactions: currentGroup });
    }

    return groups;
  }, [invoices, activeStatusFilter, selectedOperationalStatuses, selectedTrustRequestStatuses]);

  // Calculate summary statistics for filtered transactions
  const transactionSummary = useMemo(() => {
    const allTransactions = groupedInvoices.flatMap(group => group.transactions);

    // Trust Request calculations
    const trustRequestStats = {
      total: {
        sum: 0,
        count: 0,
      },
      pending: {
        sum: 0,
        count: 0,
      },
      paid: {
        sum: 0,
        count: 0,
      },
      balance: {
        sum: 0,
        count: 0,
      },
    };

    // Operational calculations
    const operationalStats = {
      total: {
        sum: 0,
        count: 0,
      },
      paid: {
        sum: 0,
        count: 0,
      },
      balance: {
        sum: 0,
        count: 0,
      },
    };

    // Status counts for both operational and trust request
    const statusCounts = {
      operational: {} as Record<string, number>,
      trustRequest: {} as Record<string, number>,
    };

    allTransactions.forEach(invoice => {
      // Trust Request calculations and status counts
      if (invoice.trustRequest && invoice.trustRequest.state !== 'not_created') {
        const trustTotal = invoice.trustRequest.total || 0;
        const trustPaid = invoice.trustRequest.paid || 0;
        const trustPending = invoice.trustRequest.pending || 0;
        const trustBalance = invoice.trustRequest.balance;
        const trustState = invoice.trustRequest.state || 'unknown';

        trustRequestStats.total.sum += trustTotal;
        trustRequestStats.total.count += 1;

        if (trustPending > 0) {
          trustRequestStats.pending.sum += trustPending;
          trustRequestStats.pending.count += 1;
        }

        if (trustPaid > 0) {
          trustRequestStats.paid.sum += trustPaid;
          trustRequestStats.paid.count += 1;
        }

        if (trustBalance !== null && trustBalance !== undefined) {
          trustRequestStats.balance.sum += trustBalance;
          trustRequestStats.balance.count += 1;
        }

        // Count by status
        statusCounts.trustRequest[trustState] = (statusCounts.trustRequest[trustState] || 0) + 1;
      }

      // Operational calculations and status counts
      const operationalTotal =
        invoice.operationalBill?.amount || invoice.operationalBill?.total || 0;
      const operationalPaid = invoice.operationalBill?.paid || 0;
      const operationalBalance = invoice.operationalBill?.balance;
      const operationalState = invoice.operationalBill?.state || 'unknown';

      operationalStats.total.sum += operationalTotal;
      operationalStats.total.count += 1;

      if (operationalPaid > 0) {
        operationalStats.paid.sum += operationalPaid;
        operationalStats.paid.count += 1;
      }

      if (operationalBalance !== null && operationalBalance !== undefined) {
        operationalStats.balance.sum += operationalBalance;
        operationalStats.balance.count += 1;
      }

      // Count by status
      statusCounts.operational[operationalState] =
        (statusCounts.operational[operationalState] || 0) + 1;
    });

    return {
      trustRequest: trustRequestStats,
      operational: operationalStats,
      statusCounts,
    };
  }, [groupedInvoices]);

  // Pagination component
  const paginationComponent = useMemo(() => {
    const displayedCount = groupedInvoices.reduce(
      (total, group) => total + group.transactions.length,
      0
    );
    console.log('🔢 Pagination Props:', {
      currentPage,
      hasNextPage,
      hasPreviousPage,
      pageSize,
      displayedCount,
      totalInvoicesFromAPI: invoices.length,
      isLoading: isLoadingInvoices,
      hasActiveFilter: !!activeStatusFilter,
    });

    return (
      <Pagination
        currentPage={currentPage}
        totalPages={999}
        totalRecords={totalRecords}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        isLoading={isLoadingInvoices}
        pageSize={pageSize}
        pageSizeOptions={[25, 50, 100, 200, 500]}
        defaultPageSizeOptions={[25, 50, 100, 200, 500]}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        cursorBased={true}
        showRecordCount={true}
        currentItemsCount={displayedCount}
      />
    );
  }, [
    currentPage,
    hasNextPage,
    hasPreviousPage,
    pageSize,
    groupedInvoices,
    invoices.length,
    isLoadingInvoices,
    activeStatusFilter,
    totalRecords,
    handlePageChange,
    handlePageSizeChange,
    handleNextPage,
    handlePreviousPage,
  ]);

  // Action handlers for the menu actions
  const resendEmail = useResendEmailMutation();
  const bulkSendEmails = useBulkSendEmailsMutation();
  const regeneratePdf = useRegeneratePdfMutation();
  const bulkRegeneratePdf = useBulkRegeneratePdfMutation();
  const bulkRefreshFromClio = useBulkRefreshFromClioMutation();
  const bulkSimulate = useBulkSimulateMutation();

  const handleBulkSend = () => {
    if (selectedTransactions.size === 0) {
      showToast.warning('No transactions selected');
      return;
    }

    // Get currently visible transactions and filter to only selected visible ones
    const visibleTransactions = groupedInvoices?.flatMap(group => group.transactions) || [];
    const visibleSelectedTransactions = visibleTransactions.filter(inv =>
      selectedTransactions.has(inv.id)
    );

    // Filter visible selected transactions for resend eligibility (trust request state must be awaiting_payment or paid) and valid recipients
    const eligibleIds = visibleSelectedTransactions
      .filter(inv => isResendEligible(inv) && hasValidRecipients(inv))
      .map(inv => inv.id);

    if (eligibleIds.length === 0) {
      showToast.warning(
        'None of the selected transactions in the current view are eligible for email sending. Only Trust Request bills in awaiting_payment or paid status with valid recipients can be emailed.'
      );
      return;
    }

    bulkSendEmails.mutate(eligibleIds);
  };

  // PDF regeneration confirmation helpers
  const openRegenerateConfirm = (type: 'single' | 'bulk', target: any, bulkIds?: string[]) => {
    setRegenerateConfirmType(type);
    setRegenerateConfirmTarget(target);
    setRegenerateConfirmBulkIds(bulkIds || []);
    setRegenerateConfirmOpen(true);
  };

  const handleRegenerateConfirm = () => {
    if (regenerateConfirmType === 'single' && regenerateConfirmTarget) {
      regeneratePdf.mutate(regenerateConfirmTarget.id);
    } else if (regenerateConfirmType === 'bulk' && regenerateConfirmBulkIds.length > 0) {
      bulkRegeneratePdf.mutate(regenerateConfirmBulkIds);
    }
    setRegenerateConfirmOpen(false);
  };

  const handleBulkRegeneratePdf = () => {
    if (selectedTransactions.size === 0) {
      showToast.warning('No transactions selected');
      return;
    }

    // Filter to only eligible transactions (TRR bills in awaiting_payment or paid status)
    const eligibleIds = Array.from(selectedTransactions).filter(id => {
      const invoice = invoices?.find(inv => inv.id === id);
      return invoice && isPdfRegenerationEligible(invoice);
    });

    if (eligibleIds.length === 0) {
      showToast.warning(
        'None of the selected transactions are eligible for PDF regeneration. Only Trust Request bills in awaiting_payment, paid, or not_required status can be regenerated.'
      );
      return;
    }

    // Open confirmation modal with eligible IDs
    openRegenerateConfirm('bulk', null, eligibleIds);
  };

  const handleBulkRefreshFromClio = () => {
    if (selectedTransactions.size === 0) {
      showToast.warning('No transactions selected');
      return;
    }

    // Get currently visible transactions and filter to only selected visible ones
    const visibleTransactions = groupedInvoices?.flatMap(group => group.transactions) || [];
    const visibleSelectedTransactions = visibleTransactions.filter(inv =>
      selectedTransactions.has(inv.id)
    );

    const selectedIds = visibleSelectedTransactions.map(inv => inv.id);

    if (selectedIds.length === 0) {
      showToast.warning('No transactions selected in the current view');
      return;
    }

    bulkRefreshFromClio.mutate(selectedIds);
  };

  const handleBulkSimulate = async (
    billType: 'operational' | 'trust',
    eventType: 'update' | 'delete'
  ) => {
    if (selectedTransactions.size === 0) {
      showToast.warning('No transactions selected');
      return;
    }

    // Get currently visible transactions and filter to only selected visible ones
    const visibleTransactions = groupedInvoices?.flatMap(group => group.transactions) || [];
    const visibleSelectedTransactions = visibleTransactions.filter(inv =>
      selectedTransactions.has(inv.id)
    );

    if (visibleSelectedTransactions.length === 0) {
      showToast.warning('No transactions selected in the current view');
      return;
    }

    // Extract juniperIds from selected transactions
    // Juniper IDs are always available in transaction data
    const juniperIds = visibleSelectedTransactions
      .map(transaction => transaction.juniperId || transaction.id)
      .filter((id): id is string => !!id);

    if (juniperIds.length === 0) {
      showToast.warning('No valid Juniper IDs found in selected transactions');
      return;
    }

    // Call the bulk simulate mutation
    bulkSimulate.mutate({
      juniperIds,
      billType,
      eventType,
      reason: `Bulk simulation from juniper transactions page: ${billType} ${eventType} events for ${juniperIds.length} transaction(s)`,
    });
  };

  const handleSelectAll = () => {
    if (!groupedInvoices || groupedInvoices.length === 0) return;

    // Get all currently visible transactions (already filtered)
    const visibleTransactions = groupedInvoices.flatMap(group => group.transactions);
    const visibleIds = visibleTransactions.map(inv => inv.id);

    // Check if all visible transactions are already selected
    const allSelected = visibleIds.every(id => selectedTransactions.has(id));

    if (allSelected && visibleIds.length > 0) {
      // Deselect all visible transactions
      setSelectedTransactions(new Set());
    } else {
      // Select all visible transactions (refresh works for any transaction)
      setSelectedTransactions(new Set(visibleIds));
    }
  };

  const handleToggleSelection = (id: string) => {
    const newSelection = new Set(selectedTransactions);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedTransactions(newSelection);
  };

  const openConfirmForTransaction = async (transaction: any) => {
    setConfirmTarget(transaction);
    setConfirmOpen(true);
    setConfirmLoading(true);
    try {
      const juniperConfig: any = await api.get('/juniper/config');
      const isOverride =
        !!juniperConfig.overrideClientEmails && !!juniperConfig.overrideEmailAddress;
      setOverrideActive(isOverride);

      const bcc: string[] = Array.isArray(juniperConfig.emailBcc)
        ? juniperConfig.emailBcc.filter(Boolean)
        : [];
      setBccRecipients(bcc);

      // Extract bill recipient emails from transaction data
      // Priority: billRecipientEmails (from matter contacts) > fallback to client email
      let actualRecipients: string[] = [];

      if (
        transaction?.billRecipientEmails &&
        Array.isArray(transaction.billRecipientEmails) &&
        transaction.billRecipientEmails.length > 0
      ) {
        // Handle both formats: array of strings or array of objects
        actualRecipients = transaction.billRecipientEmails
          .map((recipient: any) => {
            // If recipient is a string, use it directly
            if (typeof recipient === 'string') {
              return recipient;
            }
            // If recipient is an object, extract primary_email_address
            if (typeof recipient === 'object' && recipient !== null) {
              return recipient.primary_email_address;
            }
            return null;
          })
          .filter(Boolean) as string[];
      } else {
        // Fallback to client email if billRecipientEmails not available
        const clientEmail =
          transaction?.operationalBill?.client?.primary_email_address ||
          transaction?.operationalBill?.client?.email ||
          '';
        actualRecipients = clientEmail ? [clientEmail] : [];
      }

      if (isOverride) {
        setToRecipients([juniperConfig.overrideEmailAddress]);
        setActualRecipients(actualRecipients);
      } else {
        setToRecipients(actualRecipients);
        setActualRecipients([]);
      }
    } catch (_e) {
      // Error fallback: try billRecipientEmails first, then client email
      let actualRecipients: string[] = [];

      if (
        transaction?.billRecipientEmails &&
        Array.isArray(transaction.billRecipientEmails) &&
        transaction.billRecipientEmails.length > 0
      ) {
        // Handle both formats: array of strings or array of objects
        actualRecipients = transaction.billRecipientEmails
          .map((recipient: any) => {
            // If recipient is a string, use it directly
            if (typeof recipient === 'string') {
              return recipient;
            }
            // If recipient is an object, extract primary_email_address
            if (typeof recipient === 'object' && recipient !== null) {
              return recipient.primary_email_address;
            }
            return null;
          })
          .filter(Boolean) as string[];
      } else {
        const clientEmail =
          transaction?.operationalBill?.client?.primary_email_address ||
          transaction?.operationalBill?.client?.email ||
          '';
        actualRecipients = clientEmail ? [clientEmail] : [];
      }

      setToRecipients(actualRecipients);
      setBccRecipients([]);
      setOverrideActive(false);
      setActualRecipients([]);
    } finally {
      setConfirmLoading(false);
    }
  };

  // PDF viewer handlers
  const handleViewPdf = (type: 'invoice' | 'ledger') => {
    if (!downloadMenuState.transaction) return;

    const juniperId =
      downloadMenuState.transaction.juniperId || downloadMenuState.transaction.xtndTrustId;
    const url = `/juniper/document-viewer/${juniperId}?type=${type}`;

    // Open in new tab
    window.open(url, '_blank');
  };

  const handleViewReceipt = (filename: string) => {
    if (!downloadMenuState.transaction) return;

    const juniperId =
      downloadMenuState.transaction.juniperId || downloadMenuState.transaction.xtndTrustId;
    const url = `/juniper/document-viewer/${juniperId}?type=receipt&filename=${encodeURIComponent(filename)}`;

    // Open in new tab
    window.open(url, '_blank');
  };

  const handleDownloadReceiptsZip = async (transaction: any) => {
    try {
      const juniperId = transaction.juniperId || transaction.xtndTrustId || transaction.id;
      const endpoint = `/juniper-query/transactions/${juniperId}/receipts/zip`;
      const response = await api.post<{ downloadUrl: string }>(endpoint, {});

      if (response.downloadUrl) {
        // Trigger download - backend now includes proper content disposition headers
        const link = document.createElement('a');
        link.href = response.downloadUrl;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showToast.success('Receipt ZIP download started');
      }
    } catch (error) {
      console.error('Failed to download receipts ZIP:', error);
      showToast.error('Failed to download receipts ZIP');
    }
  };

  const handleQuickViewPdf = (transaction: any) => {
    const hasPdf = transaction.pdfs?.invoice;
    const isValidStatus =
      transaction.juniperStatus?.toLowerCase().includes('email held') ||
      transaction.juniperStatus?.toLowerCase().includes('completed');
    const canView = hasPdf && isValidStatus;

    if (!canView) {
      const reason = !hasPdf
        ? 'PDF not available'
        : `Transaction status must be 'Email Held' or 'Completed' (current: ${transaction.juniperStatus || 'unknown'})`;
      showToast.error(`PDF viewer not available - ${reason}`);
      return;
    }

    const juniperId = transaction.juniperId || transaction.xtndTrustId;
    const url = `/juniper/pdf-viewer/${juniperId}?type=invoice`; // Default to invoice for quick view

    // Open in new tab
    window.open(url, '_blank');
  };

  // Helper function to get trust request number
  const getTrustRequestNumber = (transaction: any) => {
    return transaction.trustRequest?.number || 'N/A';
  };

  // Helper function to format trust request calculation
  const formatTrustRequestCalculation = (invoice: any) => {
    // If trust request hasn't been created, show N/A
    if (invoice.trustRequest?.state === 'not_created') {
      return 'N/A';
    }

    // After transform, calculationData is in camelCase with camelCase properties
    const calculationData = invoice.calculationData;

    if (!calculationData) {
      return 'Calculation data not available';
    }

    // Check if we have stored calculated values - use them directly for consistency
    const { calculatedTrustAmountOpenMatter, calculatedTrustAmountClosedMatter } = calculationData;
    const matterStatus = invoice.matterStatus;

    if (
      calculatedTrustAmountOpenMatter !== undefined &&
      calculatedTrustAmountClosedMatter !== undefined &&
      matterStatus
    ) {
      // Use stored calculated values for exact consistency with creation time
      const relevantAmount =
        matterStatus === 'Closed'
          ? calculatedTrustAmountClosedMatter
          : calculatedTrustAmountOpenMatter;
      const formula =
        matterStatus === 'Closed'
          ? `${numberUtil.formatCurrency(calculationData.outstandingBalances || 0)} - ${numberUtil.formatCurrency(calculationData.trustBalances || 0)}`
          : `${numberUtil.formatCurrency(calculationData.minimumThreshold || 0)} - (${numberUtil.formatCurrency(calculationData.trustBalances || 0)} - ${numberUtil.formatCurrency(calculationData.outstandingBalances || 0)})`;

      return formula;
    }

    // Fallback to recalculation if stored values aren't available (backward compatibility)
    const { minimumThreshold, trustBalances, outstandingBalances } = calculationData;

    if (
      minimumThreshold === undefined ||
      trustBalances === undefined ||
      outstandingBalances === undefined
    ) {
      return 'Calculation data not available';
    }

    // Format the calculation with actual values from calculation data
    const calculation = `${numberUtil.formatCurrency(minimumThreshold)} - (${numberUtil.formatCurrency(trustBalances)} - ${numberUtil.formatCurrency(outstandingBalances)})`;
    return calculation;
  };

  // Helper function to copy text to clipboard
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast.success(`${label} copied to clipboard!`);
    } catch (error) {
      showToast.error('Failed to copy to clipboard');
    }
  };

  const getVisibleColumnCount = () => {
    return Object.values(visibleColumns).filter(Boolean).length;
  };

  const getTotalColumnCount = () => {
    return Object.keys(visibleColumns).length;
  };

  // Calculate dynamic colSpan values based on visible columns
  const getHeaderSpans = () => {
    // Matter Information section (always includes: warning, matterNumber, juniperStatus, lastSent)
    let matterInfoCols = 4; // Base columns always visible (warning + 3 others)
    if (visibleColumns.clientName) matterInfoCols++;
    if (visibleColumns.createdAt) matterInfoCols++;
    if (visibleColumns.updatedAt) matterInfoCols++;
    if (visibleColumns.paymentLink) matterInfoCols++;

    // Operational Bill section (always includes: number, status)
    let operationalBillCols = 2; // Base columns always visible
    if (visibleColumns.operationalBillTotal) operationalBillCols++;
    if (visibleColumns.operationalBillPaid) operationalBillCols++;
    if (visibleColumns.operationalBillBalance) operationalBillCols++;
    if (visibleColumns.operationalBillIssuedAt) operationalBillCols++;
    if (visibleColumns.operationalBillDueAt) operationalBillCols++;

    // Trust Request Bill section (always includes: number, status)
    let trustRequestBillCols = 2; // Base columns always visible
    if (visibleColumns.trustRequestBillTotal) trustRequestBillCols++;
    if (visibleColumns.trustRequestBillPaid) trustRequestBillCols++;
    if (visibleColumns.trustRequestBillBalance) trustRequestBillCols++;
    if (visibleColumns.trustRequestBillPending) trustRequestBillCols++;
    if (visibleColumns.trustRequestBillIssuedAt) trustRequestBillCols++;
    if (visibleColumns.trustRequestBillDueAt) trustRequestBillCols++;
    if (visibleColumns.trustRequestCalculation) trustRequestBillCols++;

    // Actions section (always 1)
    const actionsCols = 1;

    return {
      matterInfo: matterInfoCols,
      operationalBill: operationalBillCols,
      trustRequestBill: trustRequestBillCols,
      actions: actionsCols,
      total: matterInfoCols + operationalBillCols + trustRequestBillCols + actionsCols,
    };
  };

  // Combined loading state
  const isLoading = isLoadingInvoices || isLoadingCounts || isLoadingUsers;

  return (
    <PageLayout
      title="Juniper Transactions"
      description="View and manage invoice transactions from Clio Manage and QuickBooks Online"
      icon="HiTable"
      isLoading={isLoading}
      headerContent={
        <Button
          variant="primary"
          size="md"
          onClick={() => setIsWebhookModalOpen(true)}
          className="flex items-center justify-center w-10 h-10"
          title="Submit Request"
        >
          <Icon name="HiPlus" size="lg" />
        </Button>
      }
    >

      {/* Tab Navigation */}
      <div
        role="tablist"
        className="
          mx-auto self-center
          max-w-full w-fit
          flex flex-wrap justify-center gap-1
          bg-gray-100 dark:bg-xtnd-dark-800 border border-gray-200 dark:border-xtnd-dark-600
          p-1 rounded-lg mb-6
        "
      >
        <Button
          type="button"
          role="tab"
          aria-selected={activeTab === 'evergreen'}
          onClick={() => setActiveTab('evergreen')}
          size="sm"
          variant="ghost"
          className={`${
            activeTab === 'evergreen'
              ? 'bg-xtnd-blue text-xtnd-white shadow-sm'
              : 'text-xtnd-dark-600 dark:text-xtnd-light-400 hover:text-xtnd-dark dark:hover:text-xtnd-white hover:bg-gray-200 dark:hover:bg-xtnd-dark-700'
          }`}
          leftIcon={<Icon name="HiCollection" className="h-4 w-4" />}
        >
          Evergreen
        </Button>
        <Button
          type="button"
          role="tab"
          aria-selected={activeTab === 'trust-requests'}
          onClick={() => setActiveTab('trust-requests')}
          size="sm"
          variant="ghost"
          className={`${
            activeTab === 'trust-requests'
              ? 'bg-xtnd-blue text-xtnd-white shadow-sm'
              : 'text-xtnd-dark-600 dark:text-xtnd-light-400 hover:text-xtnd-dark dark:hover:text-xtnd-white hover:bg-gray-200 dark:hover:bg-xtnd-dark-700'
          }`}
          leftIcon={<Icon name="HiDocumentText" className="h-4 w-4" />}
        >
          Trust Requests
        </Button>
      </div>

      {/* Column Visibility Modal */}
      <ColumnVisibilityModal
        isOpen={isColumnModalOpen}
        onClose={() => setIsColumnModalOpen(false)}
        visibleColumns={visibleColumns}
        onToggleColumn={(key) => {
          setVisibleColumns((prev: Record<string, boolean>) => ({
            ...prev,
            [key]: !prev[key],
          }));
        }}
        onToggleAllColumns={(checked) => {
          setVisibleColumns((prev: Record<string, boolean>) => {
            const newState = { ...prev };
            Object.keys(newState).forEach(key => {
              newState[key] = checked;
            });
            return newState;
          });
        }}
      />

      {/* Tab Content */}
      {activeTab === 'evergreen' ? (
        <>
          {/* Collapsible Status Cards Section */}
      <Card variant="default" className="w-full">
        <CardBody>
          <button
            className="flex items-center justify-between w-full text-left focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2"
            onClick={() => setIsCardsCollapsed(!isCardsCollapsed)}
            aria-expanded={!isCardsCollapsed}
            aria-controls="cards-content"
          >
            <div className="flex items-center space-x-3">
              <Icon name="HiViewGrid" className="h-5 w-5 text-xtnd-dark dark:text-xtnd-light" />
              <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                Status Overview
              </h3>
              <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400">
                • {getSelectedCardsSummary()}
              </span>
            </div>
            <Icon
              name={isCardsCollapsed ? "HiChevronDown" : "HiChevronUp"}
              className="h-5 w-5 text-xtnd-dark-400 dark:text-xtnd-dark-500 transition-transform duration-200"
            />
          </button>

            {!isCardsCollapsed && (
              <div id="cards-content" className="mt-4">
                {/* Status Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                  <Card
                    className={`
            bg-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-700
            hover:shadow-2xl hover:shadow-red-500/20 hover:-translate-y-1
            transition-all duration-300 rounded-xtnd cursor-pointer
            ${activeStatusFilter === 'alert' ? 'ring-2 ring-red-500 shadow-lg shadow-red-500/30 -translate-y-0.5' : ''}
          `}
                    onClick={() => handleStatusCardClick('alert')}
                  >
                    <CardBody className="p-4">
                      <div className="grid grid-cols-2 grid-rows-2 gap-2 h-full min-h-[80px]">
                        <div className="col-span-2">
                          <p className="text-base font-medium text-xtnd-dark dark:text-xtnd-light">
                            Alert
                          </p>
                        </div>
                        <div className="flex items-center justify-start">
                          <Icon name="HiExclamationCircle" className="h-8 w-8 text-red-500" />
                        </div>
                        <div className="flex items-center justify-end">
                          <p className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">
                            {isLoadingCounts ? '...' : counts.alert}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  <Card
                    className={`
            bg-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-700
            hover:shadow-2xl hover:shadow-gray-500/20 hover:-translate-y-1
            transition-all duration-300 rounded-xtnd cursor-pointer
            ${activeStatusFilter === 'deletedBills' ? 'ring-2 ring-gray-500 shadow-lg shadow-gray-500/30 -translate-y-0.5' : ''}
          `}
                    onClick={() => handleStatusCardClick('deletedBills')}
                  >
                    <CardBody className="p-4">
                      <div className="grid grid-cols-2 grid-rows-2 gap-2 h-full min-h-[80px]">
                        <div className="col-span-2">
                          <p className="text-base font-medium text-xtnd-dark dark:text-xtnd-light">
                            Deleted & Voided Bills
                          </p>
                        </div>
                        <div className="flex items-center justify-start">
                          <Icon name="HiTrash" className="h-8 w-8 text-gray-500" />
                        </div>
                        <div className="flex items-center justify-end">
                          <p className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">
                            {isLoadingCounts
                              ? '...'
                              : invoices.filter((invoice: any) => {
                                  const operationalBillStatus =
                                    invoice.operationalBill?.state?.toLowerCase() || '';
                                  const trustRequestBillStatus =
                                    invoice.trustRequest?.state?.toLowerCase() || '';
                                  return (
                                    operationalBillStatus === 'deleted' ||
                                    operationalBillStatus === 'void' ||
                                    operationalBillStatus === 'voided' ||
                                    trustRequestBillStatus === 'deleted' ||
                                    trustRequestBillStatus === 'void' ||
                                    trustRequestBillStatus === 'voided'
                                  );
                                }).length}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  <Card
                    className={`
            bg-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-700
            hover:shadow-2xl hover:shadow-orange-500/20 hover:-translate-y-1
            transition-all duration-300 rounded-xtnd cursor-pointer
            ${activeStatusFilter === 'operationalPreApproval' ? 'ring-2 ring-orange-500 shadow-lg shadow-orange-500/30 -translate-y-0.5' : ''}
          `}
                    onClick={() => handleStatusCardClick('operationalPreApproval')}
                  >
                    <CardBody className="p-4">
                      <div className="grid grid-cols-2 grid-rows-2 gap-2 h-full min-h-[80px]">
                        <div className="col-span-2">
                          <p className="text-base font-medium text-xtnd-dark dark:text-xtnd-light">
                            Operational Pre Approval
                          </p>
                        </div>
                        <div className="flex items-center justify-start">
                          <Icon name="HiClipboard" className="h-8 w-8 text-orange-500" />
                        </div>
                        <div className="flex items-center justify-end">
                          <p className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">
                            {isLoadingCounts ? '...' : counts.operationalPreApproval || 0}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  <Card
                    className={`
            bg-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-700
            hover:shadow-2xl hover:shadow-purple-500/20 hover:-translate-y-1
            transition-all duration-300 rounded-xtnd cursor-pointer
            ${activeStatusFilter === 'trrCreated' ? 'ring-2 ring-purple-500 shadow-lg shadow-purple-500/30 -translate-y-0.5' : ''}
          `}
                    onClick={() => handleStatusCardClick('trrCreated')}
                  >
                    <CardBody className="p-4">
                      <div className="grid grid-cols-2 grid-rows-2 gap-2 h-full min-h-[80px]">
                        <div className="col-span-2">
                          <p className="text-base font-medium text-xtnd-dark dark:text-xtnd-light">
                            Pending TRR Approval
                          </p>
                        </div>
                        <div className="flex items-center justify-start">
                          <Icon name="HiDocumentAdd" className="h-8 w-8 text-purple-500" />
                        </div>
                        <div className="flex items-center justify-end">
                          <p className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">
                            {isLoadingCounts ? '...' : counts.trrCreated}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  <Card
                    className={`
            bg-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-700
            hover:shadow-2xl hover:shadow-yellow-500/20 hover:-translate-y-1
            transition-all duration-300 rounded-xtnd cursor-pointer
            ${activeStatusFilter === 'pending' ? 'ring-2 ring-yellow-500 shadow-lg shadow-yellow-500/30 -translate-y-0.5' : ''}
          `}
                    onClick={() => handleStatusCardClick('pending')}
                  >
                    <CardBody className="p-4">
                      <div className="grid grid-cols-2 grid-rows-2 gap-2 h-full min-h-[80px]">
                        <div className="col-span-2">
                          <p className="text-base font-medium text-xtnd-dark dark:text-xtnd-light">
                            Juniper Email on Hold
                          </p>
                        </div>
                        <div className="flex items-center justify-start">
                          <Icon name="HiClock" className="h-8 w-8 text-yellow-500" />
                        </div>
                        <div className="flex items-center justify-end">
                          <p className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">
                            {isLoadingCounts ? '...' : counts.pending}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  <Card
                    className={`
            bg-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-700
            hover:shadow-2xl hover:shadow-blue-500/20 hover:-translate-y-1
            transition-all duration-300 rounded-xtnd cursor-pointer
            ${activeStatusFilter === 'sent' ? 'ring-2 ring-blue-500 shadow-lg shadow-blue-500/30 -translate-y-0.5' : ''}
          `}
                    onClick={() => handleStatusCardClick('sent')}
                  >
                    <CardBody className="p-4">
                      <div className="grid grid-cols-2 grid-rows-2 gap-2 h-full min-h-[80px]">
                        <div className="col-span-2">
                          <p className="text-base font-medium text-xtnd-dark dark:text-xtnd-light">
                            Juniper Completed
                          </p>
                        </div>
                        <div className="flex items-center justify-start">
                          <Icon name="HiDocumentText" className="h-8 w-8 text-blue-500" />
                        </div>
                        <div className="flex items-center justify-end">
                          <p className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">
                            {isLoadingCounts ? '...' : counts.sent}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </div>

                {/* Active Filter Indicator */}
                {activeStatusFilter && (
                  <div className="flex items-center justify-center">
                    <div className="bg-xtnd-light dark:bg-xtnd-dark-700 px-4 py-2 rounded-full flex items-center space-x-2">
                      <span className="text-sm text-xtnd-dark dark:text-xtnd-light font-medium">
                        Filtering by: {getFilterDisplayName(activeStatusFilter)}
                      </span>
                      <button
                        onClick={() => setActiveStatusFilter(null)}
                        className="text-xtnd-dark dark:text-xtnd-light hover:text-red-500 transition-colors duration-200"
                        title="Clear filter"
                      >
                        <Icon name="HiChevronDown" className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Collapsible Filter Section */}
        <Card variant="default" className="w-full">
          <CardBody>
            <button
              className="flex items-center justify-between w-full text-left focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2"
              onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
              aria-expanded={!isFiltersCollapsed}
              aria-controls="filters-content"
            >
              <div className="flex items-center space-x-3">
                <Icon name="HiFilter" className="h-5 w-5 text-xtnd-dark dark:text-xtnd-light" />
                <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                  Filters
                </h3>
                <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400">
                  • {getDateRangeSummary()} • {getSelectedFiltersSummary()}
                </span>
              </div>
              <Icon
                name={isFiltersCollapsed ? 'HiChevronDown' : 'HiChevronUp'}
                className="h-5 w-5 text-xtnd-dark-400 dark:text-xtnd-dark-500 transition-transform duration-200"
              />
            </button>

            {!isFiltersCollapsed && (
              <div id="filters-content" className="mt-4">
                {/* Responsive Filter Cards Container */}
                <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
                  {/* Date Range Selector */}
                  <Card variant="default" className="w-full">
                    <CardBody>
                      <div className="flex flex-col gap-4">
                        <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                          Date Range
                        </h3>
                        <DatePicker
                          mode="range"
                          label=""
                          value={{
                            startDate: startDate ? new Date(startDate) : null,
                            endDate: endDate ? new Date(endDate) : null,
                          }}
                          onChange={range => {
                            // Only update when both dates are selected
                            if (range.startDate && range.endDate) {
                              updateDateRange(
                                dateUtil.formatDate(range.startDate, dateUtil.DateFormat.ISO),
                                dateUtil.formatDate(range.endDate, dateUtil.DateFormat.ISO)
                              );
                              resetPagination();
                            }
                          }}
                          format={dateUtil.DateFormat.US}
                        />

                        {/* Data Summary */}
                        <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400">
                          Showing {invoices.length} transaction{invoices.length !== 1 ? 's' : ''}
                          {activeStatusFilter &&
                            ` (filtered by ${getFilterDisplayName(activeStatusFilter)})`}
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  {/* Bill Status Filters */}
                  <Card variant="default" className="w-full">
                    <CardBody>
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                          Bill Status
                        </h3>

                        {/* Operational Status Filters */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2">
                            <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                            Operational Status
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { value: 'draft', label: 'Draft', color: 'bg-yellow-100' },
                              {
                                value: 'awaiting_approval',
                                label: 'Awaiting Approval',
                                color: 'bg-yellow-100',
                              },
                              {
                                value: 'awaiting_payment',
                                label: 'Awaiting Payment',
                                color: 'bg-yellow-100',
                              },
                              {
                                value: 'partial_payment',
                                label: 'Partial Payment',
                                color: 'bg-blue-100',
                              },
                              {
                                value: 'pending_payment',
                                label: 'Pending Payment',
                                color: 'bg-lime-100',
                              },
                              { value: 'paid', label: 'Paid', color: 'bg-green-100' },
                              { value: 'not_created', label: 'Not Created', color: 'bg-gray-100' },
                              { value: 'void', label: 'Void', color: 'bg-red-100' },
                              { value: 'deleted', label: 'Deleted', color: 'bg-red-100' },
                            ].map(status => (
                              <button
                                key={status.value}
                                onClick={() => handleOperationalStatusToggle(status.value)}
                                className={`
                                px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200
                                ${
                                  selectedOperationalStatuses.includes(status.value)
                                    ? `${status.color} ${getFilterButtonTextColor(status.value)} ring-2 ring-offset-1 ring-${status.color.split('-')[1]}-300`
                                    : `bg-xtnd-light dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-light hover:bg-xtnd-blue hover:text-white`
                                }
                              `}
                              >
                                {status.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Trust Request Status Filters */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-green-700 dark:text-green-300 flex items-center gap-2">
                            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                            Trust Request Status
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { value: 'not_created', label: 'Not Created', color: 'bg-gray-100' },
                              { value: 'draft', label: 'Draft', color: 'bg-yellow-100' },
                              {
                                value: 'awaiting_approval',
                                label: 'Awaiting Approval',
                                color: 'bg-yellow-100',
                              },
                              {
                                value: 'awaiting_payment',
                                label: 'Awaiting Payment',
                                color: 'bg-yellow-100',
                              },
                              {
                                value: 'partial_payment',
                                label: 'Partial Payment',
                                color: 'bg-blue-100',
                              },
                              {
                                value: 'pending_payment',
                                label: 'Pending Payment',
                                color: 'bg-lime-100',
                              },
                              { value: 'paid', label: 'Paid', color: 'bg-green-100' },
                              { value: 'void', label: 'Void', color: 'bg-red-100' },
                              { value: 'deleted', label: 'Deleted', color: 'bg-red-100' },
                            ].map(status => (
                              <button
                                key={status.value}
                                onClick={() => handleTrustRequestStatusToggle(status.value)}
                                className={`
                                px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200
                                ${
                                  selectedTrustRequestStatuses.includes(status.value)
                                    ? `${status.color} ${getFilterButtonTextColor(status.value)} ring-2 ring-offset-1 ring-${status.color.split('-')[1]}-300`
                                    : `bg-xtnd-light dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-light hover:bg-xtnd-blue hover:text-white`
                                }
                              `}
                              >
                                {status.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Clear Sub-Filters */}
                        {(selectedOperationalStatuses.length > 0 ||
                          selectedTrustRequestStatuses.length > 0 ||
                          selectedUserIds.length > 0) && (
                          <div className="flex justify-end">
                            <button
                              onClick={() => {
                                setSelectedOperationalStatuses([]);
                                setSelectedTrustRequestStatuses([]);
                                setSelectedUserIds([]);
                                updateSubFilterUrlParams([], [], []);
                                resetPagination();
                              }}
                              className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400 hover:text-red-500 transition-colors duration-200"
                            >
                              Clear all sub-filters
                            </button>
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>

                  {/* Created By Filters */}
                  <Card variant="default" className="w-full">
                    <CardBody>
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                          Created By
                        </h3>

                        {/* User Filters */}
                        <div className="space-y-2">
                          <div className="max-h-60 overflow-y-auto space-y-1">
                            {isLoadingUsers ? (
                              <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                                Loading users...
                              </div>
                            ) : usersError ? (
                              <div className="text-sm text-red-500 dark:text-red-400 py-2">
                                Error loading users:{' '}
                                {usersError instanceof Error ? usersError.message : 'Unknown error'}
                              </div>
                            ) : userNames && Object.keys(userNames).length > 0 ? (
                              Object.entries(userNames)
                                .sort(([, a], [, b]) => a.localeCompare(b))
                                .map(([userId, userName]) => (
                                  <button
                                    key={userId}
                                    onClick={() => handleUserToggle(userId)}
                                    className={`
                                  w-full text-left px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200
                                  ${
                                    selectedUserIds.includes(userId)
                                      ? 'bg-purple-100 text-purple-900 dark:bg-purple-900 dark:text-purple-200 ring-2 ring-offset-1 ring-purple-300'
                                      : 'bg-xtnd-light dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-light hover:bg-xtnd-blue hover:text-white'
                                  }
                                `}
                                  >
                                    {userName}
                                  </button>
                                ))
                            ) : (
                              <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                                No users found
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Clear User Filters */}
                        {selectedUserIds.length > 0 && (
                          <div className="flex justify-end">
                            <button
                              onClick={() => {
                                setSelectedUserIds([]);
                                updateSubFilterUrlParams(
                                  selectedOperationalStatuses,
                                  selectedTrustRequestStatuses,
                                  []
                                );
                                resetPagination();
                              }}
                              className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400 hover:text-red-500 transition-colors duration-200"
                            >
                              Clear user filters
                            </button>
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Collapsible Summary Section */}
        <Card variant="default" className="w-full">
          <CardBody>
            <button
              className="flex items-center justify-between w-full text-left focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2"
              onClick={() => setIsSummaryCollapsed(!isSummaryCollapsed)}
              aria-expanded={!isSummaryCollapsed}
              aria-controls="summary-content"
            >
              <div className="flex items-center space-x-3">
                <Icon name="HiChartBar" className="h-5 w-5 text-xtnd-dark dark:text-xtnd-light" />
                <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                  Summary
                </h3>
                <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400">
                  • {groupedInvoices.flatMap(g => g.transactions).length} transactions
                </span>
              </div>
              <Icon
                name={isSummaryCollapsed ? 'HiChevronDown' : 'HiChevronUp'}
                className="h-5 w-5 text-xtnd-dark-400 dark:text-xtnd-dark-500 transition-transform duration-200"
              />
            </button>

            {!isSummaryCollapsed && (
              <div id="summary-content" className="mt-4">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                    {/* Operational Summary */}
                    <div className="space-y-4 flex flex-col h-full">
                      <h4 className="text-md font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                        Operational Summary
                      </h4>

                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 flex-grow">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-blue-200 dark:border-blue-700">
                              <th className="text-left py-2 font-medium text-blue-800 dark:text-blue-200">
                                Status
                              </th>
                              <th className="text-right py-2 font-medium text-blue-800 dark:text-blue-200">
                                Sum
                              </th>
                              <th className="text-right py-2 font-medium text-blue-800 dark:text-blue-200">
                                Count
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-blue-100 dark:border-blue-800">
                              <td className="py-2 text-blue-700 dark:text-blue-300 font-medium">
                                Total
                              </td>
                              <td className="py-2 text-right font-mono text-blue-800 dark:text-blue-200">
                                {numberUtil.formatCurrency(
                                  transactionSummary.operational.total.sum
                                )}
                              </td>
                              <td className="py-2 text-right font-mono text-blue-800 dark:text-blue-200">
                                {transactionSummary.operational.total.count}
                              </td>
                            </tr>
                            <tr className="border-b border-blue-100 dark:border-blue-800">
                              <td className="py-2 text-green-700 dark:text-green-300 font-medium">
                                Paid
                              </td>
                              <td className="py-2 text-right font-mono text-green-800 dark:text-green-200">
                                {numberUtil.formatCurrency(transactionSummary.operational.paid.sum)}
                              </td>
                              <td className="py-2 text-right font-mono text-green-800 dark:text-green-200">
                                {transactionSummary.operational.paid.count}
                              </td>
                            </tr>
                            <tr>
                              <td className="py-2 text-purple-700 dark:text-purple-300 font-medium">
                                Balance
                              </td>
                              <td className="py-2 text-right font-mono text-purple-800 dark:text-purple-200">
                                {numberUtil.formatCurrency(
                                  transactionSummary.operational.balance.sum
                                )}
                              </td>
                              <td className="py-2 text-right font-mono text-purple-800 dark:text-purple-200">
                                {transactionSummary.operational.balance.count}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Trust Request Summary */}
                    <div className="space-y-4 flex flex-col h-full">
                      <h4 className="text-md font-medium text-green-700 dark:text-green-300 flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                        Trust Request Summary
                      </h4>

                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 flex-grow">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-green-200 dark:border-green-700">
                              <th className="text-left py-2 font-medium text-green-800 dark:text-green-200">
                                Status
                              </th>
                              <th className="text-right py-2 font-medium text-green-800 dark:text-green-200">
                                Sum
                              </th>
                              <th className="text-right py-2 font-medium text-green-800 dark:text-green-200">
                                Count
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-green-100 dark:border-green-800">
                              <td className="py-2 text-green-700 dark:text-green-300 font-medium">
                                Total
                              </td>
                              <td className="py-2 text-right font-mono text-green-800 dark:text-green-200">
                                {numberUtil.formatCurrency(
                                  transactionSummary.trustRequest.total.sum
                                )}
                              </td>
                              <td className="py-2 text-right font-mono text-green-800 dark:text-green-200">
                                {transactionSummary.trustRequest.total.count}
                              </td>
                            </tr>
                            <tr className="border-b border-green-100 dark:border-green-800">
                              <td className="py-2 text-yellow-700 dark:text-yellow-300 font-medium">
                                Pending
                              </td>
                              <td className="py-2 text-right font-mono text-yellow-800 dark:text-yellow-200">
                                {numberUtil.formatCurrency(
                                  transactionSummary.trustRequest.pending.sum
                                )}
                              </td>
                              <td className="py-2 text-right font-mono text-yellow-800 dark:text-yellow-200">
                                {transactionSummary.trustRequest.pending.count}
                              </td>
                            </tr>
                            <tr className="border-b border-green-100 dark:border-green-800">
                              <td className="py-2 text-blue-700 dark:text-blue-300 font-medium">
                                Paid
                              </td>
                              <td className="py-2 text-right font-mono text-blue-800 dark:text-blue-200">
                                {numberUtil.formatCurrency(
                                  transactionSummary.trustRequest.paid.sum
                                )}
                              </td>
                              <td className="py-2 text-right font-mono text-blue-800 dark:text-blue-200">
                                {transactionSummary.trustRequest.paid.count}
                              </td>
                            </tr>
                            <tr>
                              <td className="py-2 text-purple-700 dark:text-purple-300 font-medium">
                                Balance
                              </td>
                              <td className="py-2 text-right font-mono text-purple-800 dark:text-purple-200">
                                {numberUtil.formatCurrency(
                                  transactionSummary.trustRequest.balance.sum
                                )}
                              </td>
                              <td className="py-2 text-right font-mono text-purple-800 dark:text-purple-200">
                                {transactionSummary.trustRequest.balance.count}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Status Counts Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-grow items-stretch">
                      {/* Operational Status Counts */}
                      <div className="space-y-4 flex flex-col">
                        <h4 className="text-md font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2">
                          <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                          Operational Status
                        </h4>

                        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 flex-grow">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-gray-300 dark:border-gray-600">
                                <th className="text-left py-2 font-medium text-gray-700 dark:text-gray-300">
                                  Status
                                </th>
                                <th className="text-right py-2 font-medium text-gray-700 dark:text-gray-300">
                                  Count
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(transactionSummary.statusCounts.operational)
                                .filter(([, count]) => count > 0)
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([status, count]) => (
                                  <tr
                                    key={`operational-${status}`}
                                    className="border-b border-gray-200 dark:border-gray-700"
                                  >
                                    <td className="py-2 font-medium capitalize">
                                      <span className={`${getStatusCountTextColor(status)}`}>
                                        {status.replace(/_/g, ' ')}
                                      </span>
                                    </td>
                                    <td className="py-2 text-right font-mono text-gray-800 dark:text-gray-200">
                                      {count}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Trust Request Status Counts */}
                      <div className="space-y-4 flex flex-col">
                        <h4 className="text-md font-medium text-green-700 dark:text-green-300 flex items-center gap-2">
                          <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                          Trust Request Status
                        </h4>

                        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 flex-grow">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-gray-300 dark:border-gray-600">
                                <th className="text-left py-2 font-medium text-gray-700 dark:text-gray-300">
                                  Status
                                </th>
                                <th className="text-right py-2 font-medium text-gray-700 dark:text-gray-300">
                                  Count
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(transactionSummary.statusCounts.trustRequest)
                                .filter(([, count]) => count > 0)
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([status, count]) => (
                                  <tr
                                    key={`trust-${status}`}
                                    className="border-b border-gray-200 dark:border-gray-700"
                                  >
                                    <td className="py-2 font-medium capitalize">
                                      <span className={`${getStatusCountTextColor(status)}`}>
                                        {status.replace(/_/g, ' ')}
                                      </span>
                                    </td>
                                    <td className="py-2 text-right font-mono text-gray-800 dark:text-gray-200">
                                      {count}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Main Transactions Table */}
        <div id="juniper-transactions-table-container" className="w-full max-w-full">
          <Card id="juniper-transactions-table-card" variant="default" className="w-full shadow-sm">
            <CardHeader className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-xtnd-dark-800">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-4">
                  <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-light">
                    Transactions
                  </h2>
                  {selectedTransactions.size > 0 && (
                    <div className="flex items-center space-x-2 px-3 py-1.5 bg-xtnd-blue-50 dark:bg-xtnd-blue-900/20 rounded-full">
                      <span className="text-sm font-medium text-xtnd-blue dark:text-xtnd-blue-300">
                        {selectedTransactions.size} selected
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsColumnModalOpen(true)}
                    className="flex items-center space-x-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                    title="Edit columns"
                  >
                    <Icon name="HiViewGrid" className="h-4 w-4" />
                    <span className="hidden sm:inline">Edit Columns</span>
                    <span className="inline-flex items-center justify-center text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded min-w-[2rem]">
                      {getVisibleColumnCount()}/{getTotalColumnCount()}
                    </span>
                  </Button>
                  {selectedTransactions.size > 0 &&
                    (() => {
                      // Get currently visible transactions
                      const visibleTransactions =
                        groupedInvoices?.flatMap(group => group.transactions) || [];
                      const visibleTransactionIds = new Set(visibleTransactions.map(inv => inv.id));

                      // Only count selected transactions that are currently visible
                      const visibleSelectedTransactions = visibleTransactions.filter(inv =>
                        selectedTransactions.has(inv.id)
                      );

                      // Calculate eligible counts for visible selected transactions only
                      // Email eligibility: must have trust request state awaiting_payment or paid AND valid recipients
                      const eligibleEmailCount = visibleSelectedTransactions.filter(
                        inv => isResendEligible(inv) && hasValidRecipients(inv)
                      ).length;

                      const eligibleRefreshCount = visibleSelectedTransactions.length; // Refresh available for all visible selected

                      const eligiblePdfCount =
                        visibleSelectedTransactions.filter(isPdfRegenerationEligible).length;

                      return (
                        <>
                          {eligibleEmailCount > 0 && (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={handleBulkSend}
                              disabled={bulkSendEmails.isPending}
                              className="flex items-center space-x-2"
                              title={`Send ${eligibleEmailCount} email${eligibleEmailCount > 1 ? 's' : ''} to eligible transactions in current view`}
                            >
                              <Icon name="HiMail" className="h-4 w-4" />
                              <span>
                                Send {eligibleEmailCount} Email{eligibleEmailCount > 1 ? 's' : ''}
                                {eligibleEmailCount < visibleSelectedTransactions.length && (
                                  <span className="text-xs opacity-75 ml-1">
                                    ({eligibleEmailCount}/{visibleSelectedTransactions.length})
                                  </span>
                                )}
                              </span>
                            </Button>
                          )}
                          {eligiblePdfCount > 0 && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={handleBulkRegeneratePdf}
                              disabled={bulkRegeneratePdf.isPending}
                              className="flex items-center space-x-2"
                              title={`Regenerate PDF for ${eligiblePdfCount} eligible transaction${eligiblePdfCount > 1 ? 's' : ''} (TRR awaiting payment, paid, or not required)`}
                            >
                              <Icon name="HiRefresh" className="h-4 w-4" />
                              <span>
                                Regenerate {eligiblePdfCount} PDF{eligiblePdfCount > 1 ? 's' : ''}
                                {eligiblePdfCount < selectedTransactions.size && (
                                  <span className="text-xs opacity-75 ml-1">
                                    ({eligiblePdfCount}/{selectedTransactions.size})
                                  </span>
                                )}
                              </span>
                            </Button>
                          )}
                          {eligibleRefreshCount > 0 && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={handleBulkRefreshFromClio}
                              disabled={bulkRefreshFromClio.isPending}
                              className="flex items-center space-x-2"
                              title={`Refresh ${eligibleRefreshCount} transaction${eligibleRefreshCount > 1 ? 's' : ''} from Clio in current view`}
                            >
                              <Icon name="HiRefresh" className="h-4 w-4" />
                              <span>Refresh {eligibleRefreshCount} From Clio</span>
                            </Button>
                          )}
                          {visibleSelectedTransactions.length > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setBulkSimulateModalOpen(true)}
                              className="flex items-center space-x-2"
                              title={`Simulate events for ${visibleSelectedTransactions.length} selected transaction${visibleSelectedTransactions.length > 1 ? 's' : ''}`}
                            >
                              <Icon name="HiLightningBolt" className="h-4 w-4" />
                              <span>
                                Simulate {visibleSelectedTransactions.length} Event
                                {visibleSelectedTransactions.length > 1 ? 's' : ''}
                              </span>
                            </Button>
                          )}
                        </>
                      );
                    })()}
                </div>
              </div>
            </CardHeader>
            <CardBody className="p-0">
              <div className="overflow-x-auto overflow-y-auto relative juniper-transactions-scroll max-h-[calc(100vh-300px)] min-h-[700px]">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 relative">
                  <thead className="bg-white dark:bg-gray-800 sticky top-0 z-30">
                    {/* Section Header Row */}
                    <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                      <th className="px-4 py-3 w-12 bg-gray-100 dark:bg-gray-700"></th>
                      <th
                        colSpan={getHeaderSpans().matterInfo}
                        className="px-6 py-3 text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider text-left bg-gray-100 dark:bg-gray-700 border-r-2 border-gray-300 dark:border-gray-600"
                      >
                        Basic Information
                      </th>
                      <th
                        colSpan={getHeaderSpans().operationalBill}
                        className="px-6 py-3 text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wider text-left bg-blue-100 dark:bg-blue-900/30 border-r-2 border-gray-300 dark:border-gray-600"
                      >
                        Operational Bill
                      </th>
                      <th
                        colSpan={getHeaderSpans().trustRequestBill}
                        className="px-6 py-3 text-xs font-bold text-green-700 dark:text-green-300 uppercase tracking-wider text-left bg-green-100 dark:bg-green-900/30 border-r-2 border-gray-300 dark:border-gray-600"
                      >
                        Trust Request Bill
                      </th>
                      <th
                        colSpan={getHeaderSpans().actions}
                        className="py-3 px-3 text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider text-center bg-gray-100 dark:bg-gray-700 w-16 sticky right-0 z-40 shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)]"
                      >
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>

                    {/* Column Headers Row */}
                    <tr className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                      {/* Checkbox Column */}
                      <th className="px-4 py-3 w-12 bg-gray-50 dark:bg-gray-800/50">
                        <div className="flex items-center justify-center space-x-2">
                          <input
                            type="checkbox"
                            checked={
                              groupedInvoices &&
                              groupedInvoices.length > 0 &&
                              (() => {
                                const visibleTransactions = groupedInvoices.flatMap(
                                  group => group.transactions
                                );
                                const visibleIds = visibleTransactions.map(inv => inv.id);
                                return (
                                  visibleIds.length > 0 &&
                                  visibleIds.every(id => selectedTransactions.has(id))
                                );
                              })()
                            }
                            onChange={handleSelectAll}
                            className="h-4 w-4 text-xtnd-blue border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-xtnd-blue cursor-pointer"
                            title={
                              groupedInvoices && groupedInvoices.length > 0
                                ? (() => {
                                    const visibleTransactions = groupedInvoices.flatMap(
                                      group => group.transactions
                                    );
                                    const visibleCount = visibleTransactions.length;
                                    return visibleCount === 0
                                      ? 'No transactions in current view'
                                      : `Select all ${visibleCount} transaction${visibleCount > 1 ? 's' : ''} in current view`;
                                  })()
                                : 'No transactions in current view'
                            }
                            disabled={
                              !groupedInvoices ||
                              groupedInvoices.length === 0 ||
                              (() => {
                                const visibleTransactions = groupedInvoices.flatMap(
                                  group => group.transactions
                                );
                                return visibleTransactions.length === 0;
                              })()
                            }
                          />
                          <button
                            onClick={() => {
                              refetchInvoices();
                              refetchStatusCounts();
                            }}
                            disabled={isFetchingInvoices}
                            className="p-1 text-gray-400 hover:text-xtnd-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded"
                            title="Refresh transactions and status counts"
                          >
                            <Icon
                              name="HiRefresh"
                              className={`h-4 w-4 ${isFetchingInvoices ? 'animate-spin' : ''}`}
                            />
                          </button>
                        </div>
                      </th>
                      {/* Indicators Column Header */}
                      <th className="px-2 py-3 w-16 text-center bg-gray-50 dark:bg-gray-800/50">
                        <Icon
                          name="HiInformationCircle"
                          className="h-4 w-4 text-gray-500 dark:text-gray-400 mx-auto"
                          title="Indicators (Warnings & Receipts)"
                        />
                      </th>
                      {/* Matter Information */}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider bg-gray-50/50 dark:bg-gray-800/30">
                        Matter
                      </th>
                      {visibleColumns.clientName && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider bg-gray-50/50 dark:bg-gray-800/30 min-w-[200px]">
                          Client
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider bg-gray-50/50 dark:bg-gray-800/30 min-w-[180px]">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider bg-gray-50/50 dark:bg-gray-800/30 min-w-[150px]">
                        Last Sent
                      </th>
                      {visibleColumns.createdAt && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider bg-gray-50/50 dark:bg-gray-800/30 min-w-[150px]">
                          Created
                        </th>
                      )}
                      {visibleColumns.updatedAt && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider bg-gray-50/50 dark:bg-gray-800/30 min-w-[150px]">
                          Updated
                        </th>
                      )}
                      {visibleColumns.paymentLink && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider bg-gray-50/50 dark:bg-gray-800/30 border-r-2 border-gray-200 dark:border-gray-700 min-w-[200px]">
                          Payment Link
                        </th>
                      )}

                      {/* Operational Bill Columns */}
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 min-w-[120px]">
                        Number
                      </th>
                      {visibleColumns.operationalBillTotal && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 min-w-[120px]">
                          Total
                        </th>
                      )}
                      {visibleColumns.operationalBillPaid && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 min-w-[120px]">
                          Paid
                        </th>
                      )}
                      {visibleColumns.operationalBillBalance && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 min-w-[120px]">
                          Balance
                        </th>
                      )}
                      {visibleColumns.operationalBillIssuedAt && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 min-w-[120px]">
                          Issued
                        </th>
                      )}
                      {visibleColumns.operationalBillDueAt && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 min-w-[120px]">
                          Due
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 border-r-2 border-gray-200 dark:border-gray-700 min-w-[200px]">
                        Status
                      </th>

                      {/* Trust Request Bill Columns */}
                      <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                        TRR Number
                      </th>
                      {visibleColumns.trustRequestBillTotal && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                          Total
                        </th>
                      )}
                      {visibleColumns.trustRequestBillPaid && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                          Paid
                        </th>
                      )}
                      {visibleColumns.trustRequestBillBalance && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                          Balance
                        </th>
                      )}
                      {visibleColumns.trustRequestBillPending && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                          Pending
                        </th>
                      )}
                      {visibleColumns.trustRequestBillIssuedAt && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                          Issued
                        </th>
                      )}
                      {visibleColumns.trustRequestBillDueAt && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                          Due
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[200px]">
                        Status
                      </th>
                      {visibleColumns.trustRequestCalculation && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 border-r-2 border-gray-200 dark:border-gray-700 min-w-[240px]">
                          Calculation
                        </th>
                      )}

                      {/* Actions - Sticky Right */}
                      <th className="py-3 px-3 text-center bg-white dark:bg-gray-800 w-16 sticky right-0 z-40 shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)]">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {isLoadingInvoices ? (
                      <tr>
                        <td
                          colSpan={getHeaderSpans().total}
                          className="px-6 py-8 text-center text-xtnd-dark-500 dark:text-xtnd-dark-400"
                        >
                          Loading transactions...
                        </td>
                      </tr>
                    ) : invoicesError ? (
                      <tr>
                        <td
                          colSpan={getHeaderSpans().total}
                          className="px-6 py-8 text-center text-red-500 dark:text-red-400"
                        >
                          Error loading transactions: {invoicesError.message}
                        </td>
                      </tr>
                    ) : groupedInvoices.reduce(
                        (total, group) => total + group.transactions.length,
                        0
                      ) === 0 ? (
                      <tr>
                        <td
                          colSpan={getHeaderSpans().total}
                          className="px-6 py-8 text-center text-xtnd-dark-500 dark:text-xtnd-dark-400"
                        >
                          No transactions found for the selected date range.
                        </td>
                      </tr>
                    ) : (
                      groupedInvoices.map(group => (
                        <React.Fragment key={group.period}>
                          {/* Bill Run Header */}
                          <tr>
                            <td
                              colSpan={getHeaderSpans().total}
                              className="px-6 py-3 bg-xtnd-blue-50 dark:bg-xtnd-dark-700 border-t-2 border-xtnd-blue-200 dark:border-xtnd-dark-600"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <div className="w-3 h-3 rounded-full bg-xtnd-blue"></div>
                                  <h3 className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
                                    {group.period}
                                  </h3>
                                  <span className="text-xs text-xtnd-dark-600 dark:text-xtnd-dark-400">
                                    ({group.transactions.length} transactions)
                                  </span>
                                </div>
                              </div>
                            </td>
                          </tr>

                          {/* Transactions in this group */}
                          {group.transactions.map(invoice => (
                            <tr
                              key={invoice.id}
                              className="group hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                            >
                              {/* Checkbox Column */}
                              <td className="px-4 py-4 text-center w-12 bg-gray-50 dark:bg-gray-800">
                                <input
                                  type="checkbox"
                                  checked={selectedTransactions.has(invoice.id)}
                                  onChange={() => handleToggleSelection(invoice.id)}
                                  className="h-4 w-4 text-xtnd-blue border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-xtnd-blue cursor-pointer"
                                  title="Select transaction for bulk actions"
                                />
                              </td>
                              {/* Indicators Column - Fixed left position (Warning + Receipts) */}
                              <td className="px-2 py-4 text-center w-16 bg-gray-50 dark:bg-gray-800">
                                <div className="flex items-center justify-center space-x-1.5">
                                  {/* Warning Indicator */}
                                  {(() => {
                                    const warnings = getTransactionWarnings(invoice);
                                    return warnings.length > 0 ? (
                                      <div className="relative group/warning inline-block">
                                        <Icon
                                          name="HiExclamation"
                                          className="h-4 w-4 text-amber-500"
                                          title="Transaction has warnings"
                                        />
                                        {/* Tooltip on hover */}
                                        <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover/warning:block z-50 w-72 bg-amber-50 dark:bg-amber-900/95 border border-amber-200 dark:border-amber-700 rounded-lg shadow-xl p-3">
                                          <div className="text-xs text-left">
                                            <div className="font-semibold text-amber-800 dark:text-amber-200 mb-2 flex items-center space-x-1">
                                              <Icon name="HiExclamation" className="h-4 w-4" />
                                              <span>Warnings</span>
                                            </div>
                                            <ul className="space-y-1.5 text-amber-700 dark:text-amber-300">
                                              {warnings.map((warning, idx) => (
                                                <li
                                                  key={idx}
                                                  className="flex items-start space-x-2"
                                                >
                                                  <span className="text-amber-600 dark:text-amber-400">
                                                    •
                                                  </span>
                                                  <span>{warning}</span>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        </div>
                                      </div>
                                    ) : null;
                                  })()}

                                  {/* Receipt Count Indicator */}
                                  {(() => {
                                    const receiptCount =
                                      invoice.documents?.storedAssets?.receipts?.length || 0;
                                    return receiptCount > 0 ? (
                                      <div
                                        className="flex items-center space-x-0.5 text-green-600 dark:text-green-400 cursor-help"
                                        title={`${receiptCount} receipt${receiptCount > 1 ? 's' : ''} attached`}
                                      >
                                        <Icon name="HiDocumentText" className="h-3.5 w-3.5" />
                                        <span className="text-xs font-semibold">
                                          {receiptCount}
                                        </span>
                                      </div>
                                    ) : null;
                                  })()}
                                </div>
                              </td>
                              {/* Matter Information */}
                              <td className="px-6 py-4 text-sm">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => {
                                      window.open(
                                        `https://app.clio.com/matters/${invoice.matterId}`,
                                        'clio-tab'
                                      );
                                    }}
                                    className="flex items-center space-x-1 text-xtnd-blue hover:text-xtnd-blue-600 dark:text-xtnd-blue-300 dark:hover:text-xtnd-blue-200 hover:underline focus:outline-none focus:ring-2 focus:ring-xtnd-blue rounded transition-colors"
                                  >
                                    <span className="font-medium max-w-xs truncate">
                                      {invoice.matterName || 'Unknown Matter'}
                                    </span>
                                    {invoice.matterStatus?.toLowerCase() === 'closed' && (
                                      <Icon
                                        name="HiLockClosed"
                                        className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500"
                                        title="Closed Matter"
                                      />
                                    )}
                                    <Icon
                                      name="HiExternalLink"
                                      className="h-3.5 w-3.5 flex-shrink-0"
                                    />
                                  </button>
                                  {invoice.matterId && (
                                    <Button
                                      onClick={() =>
                                        copyToClipboard(invoice.matterId.toString(), 'Matter ID')
                                      }
                                      variant="ghost"
                                      size="sm"
                                      className="p-1 h-auto opacity-0 group-hover:opacity-100 transition-opacity"
                                      title="Copy Matter ID"
                                    >
                                      <Icon
                                        name="HiClipboard"
                                        className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                                      />
                                    </Button>
                                  )}
                                </div>
                              </td>
                              {visibleColumns.clientName && (
                                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 min-w-[200px]">
                                  <span className="font-medium">
                                    {invoice.client || 'Unknown Client'}
                                  </span>
                                </td>
                              )}
                              <td className="px-6 py-4 text-sm min-w-[180px]">
                                <span
                                  className={`${getJuniperStatusColor(invoice.juniperStatus || '')} whitespace-nowrap`}
                                >
                                  {invoice.juniperStatus || 'Unknown'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[150px] responsive-datetime">
                                {invoice.lastSentAt ? (
                                  <div className="flex flex-col sm:block">
                                    <div className="font-medium">
                                      {formatResponsiveDateTime(invoice.lastSentAt).date}
                                    </div>
                                    <div className="text-xs text-gray-500 sm:hidden">
                                      {formatResponsiveDateTime(invoice.lastSentAt).time}
                                    </div>
                                  </div>
                                ) : (
                                  'Not sent'
                                )}
                              </td>
                              {visibleColumns.createdAt && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[150px] responsive-datetime">
                                  {invoice.createdAt ? (
                                    <div className="flex flex-col sm:block">
                                      <div className="font-medium">
                                        {formatResponsiveDateTime(invoice.createdAt).date}
                                      </div>
                                      <div className="text-xs text-gray-500 sm:hidden">
                                        {formatResponsiveDateTime(invoice.createdAt).time}
                                      </div>
                                    </div>
                                  ) : (
                                    ''
                                  )}
                                </td>
                              )}
                              {visibleColumns.updatedAt && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[150px] responsive-datetime">
                                  {invoice.updatedAt ? (
                                    <div className="flex flex-col sm:block">
                                      <div className="font-medium">
                                        {formatResponsiveDateTime(invoice.updatedAt).date}
                                      </div>
                                      <div className="text-xs text-gray-500 sm:hidden">
                                        {formatResponsiveDateTime(invoice.updatedAt).time}
                                      </div>
                                    </div>
                                  ) : (
                                    ''
                                  )}
                                </td>
                              )}
                              {visibleColumns.paymentLink && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[200px] border-r-2 border-gray-200 dark:border-gray-600">
                                  {(() => {
                                    const hasPaymentLink =
                                      invoice.clioPaymentLinkUrl && invoice.clioPaymentLinkId;
                                    const isPaid =
                                      invoice.trustRequest?.trrStatus?.toLowerCase() === 'paid';

                                    if (!hasPaymentLink) {
                                      return (
                                        <span className="text-gray-400 dark:text-gray-500">
                                          No link
                                        </span>
                                      );
                                    }

                                    return (
                                      <div className="flex items-center space-x-2">
                                        <a
                                          href={invoice.clioPaymentLinkUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`hover:underline flex items-center space-x-1 ${
                                            isPaid
                                              ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60'
                                              : 'text-xtnd-blue hover:text-xtnd-blue-600 dark:text-xtnd-blue-300 dark:hover:text-xtnd-blue-200'
                                          }`}
                                          onClick={e => {
                                            if (isPaid) {
                                              e.preventDefault();
                                              showToast.warning(
                                                'Payment link is not active for paid transactions'
                                              );
                                            }
                                          }}
                                          title={
                                            isPaid
                                              ? 'Payment link not active - transaction is paid'
                                              : 'Open payment link'
                                          }
                                        >
                                          <span className="truncate max-w-[120px]">
                                            {invoice.clioPaymentLinkId}
                                          </span>
                                          <Icon
                                            name="HiExternalLink"
                                            className="h-3.5 w-3.5 flex-shrink-0"
                                          />
                                        </a>
                                        {isPaid && (
                                          <Icon
                                            name="HiLockClosed"
                                            className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0"
                                            title="Link inactive - transaction is paid"
                                          />
                                        )}
                                      </div>
                                    );
                                  })()}
                                </td>
                              )}

                              {/* Operational Bill Section */}
                              <td className="px-6 py-4 text-sm font-medium text-blue-600 dark:text-blue-400 min-w-[120px]">
                                <div className="flex items-center space-x-2">
                                  <a
                                    href={`https://app.clio.com/bills/${invoice.operationalBill?.id || ''}`}
                                    target="clio-tab"
                                    rel="noopener noreferrer"
                                    className="hover:underline"
                                  >
                                    {invoice.operationalBill?.invoiceNumber || 'N/A'}
                                  </a>
                                  {invoice.operationalBill?.id && (
                                    <Button
                                      onClick={() =>
                                        copyToClipboard(
                                          invoice.operationalBill!.id.toString(),
                                          'Operational Bill ID'
                                        )
                                      }
                                      variant="ghost"
                                      size="sm"
                                      className="p-1 h-auto"
                                      title="Copy Operational Bill ID"
                                    >
                                      <Icon
                                        name="HiClipboard"
                                        className="h-3 w-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                      />
                                    </Button>
                                  )}
                                </div>
                              </td>
                              {visibleColumns.operationalBillTotal && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px]">
                                  {numberUtil.formatCurrency(invoice.operationalBill?.amount)}
                                </td>
                              )}
                              {visibleColumns.operationalBillPaid && (
                                <td className="px-6 py-4 text-sm text-green-600 dark:text-green-400 min-w-[120px]">
                                  {numberUtil.formatCurrency(invoice.operationalBill?.paid)}
                                </td>
                              )}
                              {visibleColumns.operationalBillBalance && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px]">
                                  {numberUtil.formatCurrency(invoice.operationalBill?.balance)}
                                </td>
                              )}
                              {visibleColumns.operationalBillIssuedAt && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px] responsive-datetime">
                                  {invoice.operationalBill?.issuedDate ? (
                                    <div className="flex flex-col sm:block">
                                      <div className="font-medium">
                                        {
                                          formatResponsiveDateTime(
                                            invoice.operationalBill.issuedDate
                                          ).date
                                        }
                                      </div>
                                      <div className="text-xs text-gray-500 sm:hidden">
                                        {
                                          formatResponsiveDateTime(
                                            invoice.operationalBill.issuedDate
                                          ).time
                                        }
                                      </div>
                                    </div>
                                  ) : (
                                    ''
                                  )}
                                </td>
                              )}
                              {visibleColumns.operationalBillDueAt && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px] responsive-datetime">
                                  {invoice.operationalBill?.dueDate ? (
                                    <div className="flex flex-col sm:block">
                                      <div className="font-medium">
                                        {
                                          formatResponsiveDateTime(invoice.operationalBill.dueDate)
                                            .date
                                        }
                                      </div>
                                      <div className="text-xs text-gray-500 sm:hidden">
                                        {
                                          formatResponsiveDateTime(invoice.operationalBill.dueDate)
                                            .time
                                        }
                                      </div>
                                    </div>
                                  ) : (
                                    ''
                                  )}
                                </td>
                              )}
                              <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[200px] border-r-2 border-gray-200 dark:border-gray-600">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full text-center status-badge-mobile ${getTrustRequestStatusColor(
                                    formatRawStatus(invoice.operationalBill?.status)
                                  )}`}
                                >
                                  {formatRawStatus(invoice.operationalBill?.status)}
                                </span>
                              </td>

                              {/* Trust Request Bill Section */}
                              <td className="px-6 py-4 text-sm font-medium min-w-[120px]">
                                <div className="flex items-center space-x-2">
                                  {getTrustRequestNumber(invoice) &&
                                  getTrustRequestNumber(invoice) !== 'N/A' ? (
                                    <>
                                      <a
                                        href={`https://app.clio.com/bills/${invoice.trustRequest?.id || ''}`}
                                        target="clio-tab"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 dark:text-blue-400 hover:underline"
                                      >
                                        {getTrustRequestNumber(invoice)}
                                      </a>
                                      {invoice.trustRequest?.id && (
                                        <Button
                                          onClick={() =>
                                            copyToClipboard(
                                              invoice.trustRequest!.id.toString(),
                                              'Trust Request Bill ID'
                                            )
                                          }
                                          variant="ghost"
                                          size="sm"
                                          className="p-1 h-auto"
                                          title="Copy Trust Request Bill ID"
                                        >
                                          <Icon
                                            name="HiClipboard"
                                            className="h-3 w-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                          />
                                        </Button>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-xtnd-dark dark:text-xtnd-light">N/A</span>
                                  )}
                                </div>
                              </td>
                              {visibleColumns.trustRequestBillTotal && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px]">
                                  {invoice.trustRequest?.state === 'not_created'
                                    ? '-'
                                    : numberUtil.formatCurrency(invoice.trustRequest?.total)}
                                </td>
                              )}
                              {visibleColumns.trustRequestBillPaid && (
                                <td className="px-6 py-4 text-sm text-green-600 dark:text-green-400 min-w-[120px]">
                                  {invoice.trustRequest?.state === 'not_created'
                                    ? '-'
                                    : numberUtil.formatCurrency(invoice.trustRequest?.paid)}
                                </td>
                              )}
                              {visibleColumns.trustRequestBillBalance && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px]">
                                  {invoice.trustRequest?.state === 'not_created'
                                    ? '-'
                                    : numberUtil.formatCurrency(invoice.trustRequest?.balance)}
                                </td>
                              )}
                              {visibleColumns.trustRequestBillPending && (
                                <td className="px-6 py-4 text-sm text-yellow-600 dark:text-yellow-400 min-w-[120px]">
                                  {invoice.trustRequest?.state === 'not_created'
                                    ? '-'
                                    : numberUtil.formatCurrency(invoice.trustRequest?.pending)}
                                </td>
                              )}
                              {visibleColumns.trustRequestBillIssuedAt && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px] responsive-datetime">
                                  {invoice.trustRequest?.issued_at ? (
                                    <div className="flex flex-col sm:block">
                                      <div className="font-medium">
                                        {
                                          formatResponsiveDateTime(invoice.trustRequest.issued_at)
                                            .date
                                        }
                                      </div>
                                      <div className="text-xs text-gray-500 sm:hidden">
                                        {
                                          formatResponsiveDateTime(invoice.trustRequest.issued_at)
                                            .time
                                        }
                                      </div>
                                    </div>
                                  ) : (
                                    ''
                                  )}
                                </td>
                              )}
                              {visibleColumns.trustRequestBillDueAt && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px] responsive-datetime">
                                  {invoice.trustRequest?.due_at ? (
                                    <div className="flex flex-col sm:block">
                                      <div className="font-medium">
                                        {formatResponsiveDateTime(invoice.trustRequest.due_at).date}
                                      </div>
                                      <div className="text-xs text-gray-500 sm:hidden">
                                        {formatResponsiveDateTime(invoice.trustRequest.due_at).time}
                                      </div>
                                    </div>
                                  ) : (
                                    ''
                                  )}
                                </td>
                              )}
                              <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[200px]">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full text-center status-badge-mobile ${getTrustRequestStatusColor(
                                    formatRawStatus(invoice.trustRequest?.trrStatus)
                                  )}`}
                                >
                                  {formatRawStatus(invoice.trustRequest?.trrStatus)}
                                </span>
                              </td>

                              {/* Trust Request Calculation */}
                              {visibleColumns.trustRequestCalculation && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[240px] border-r-2 border-gray-200 dark:border-gray-600">
                                  <span className="font-mono text-xs whitespace-nowrap">
                                    {formatTrustRequestCalculation(invoice)}
                                  </span>
                                </td>
                              )}

                              {/* Actions - Dropdown Menu - Sticky Right */}
                              <td
                                className={`py-4 px-3 text-sm relative bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700/50 sticky right-0 shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)] transition-colors ${
                                  actionsMenuOpen === invoice.id ? 'z-50' : 'z-10'
                                }`}
                              >
                                <div className="flex items-center justify-center space-x-2">
                                  {reprocessing.has(invoice.id) ? (
                                    <Icon
                                      name="HiRefresh"
                                      className="h-5 w-5 animate-spin text-xtnd-blue"
                                    />
                                  ) : (
                                    <button
                                      onClick={e => {
                                        const newState =
                                          actionsMenuOpen === invoice.id ? null : invoice.id;
                                        setActionsMenuOpen(newState);
                                        setMenuTriggerElement(newState ? e.currentTarget : null);
                                      }}
                                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                      title="More actions"
                                    >
                                      <Icon
                                        name="HiDotsVertical"
                                        className="h-5 w-5 text-gray-500 dark:text-gray-400"
                                      />
                                    </button>
                                  )}

                                  {/* Dropdown Menu */}
                                  <ActionsMenu
                                    isOpen={actionsMenuOpen === invoice.id}
                                    onClose={() => setActionsMenuOpen(null)}
                                    triggerElement={menuTriggerElement}
                                  >
                                    {/* View in Clio */}
                                    <ActionsMenuItem
                                      icon="HiFolder"
                                      iconColor="text-xtnd-blue"
                                      label="View in Clio"
                                      onClick={() => {
                                        const matterId = invoice.matterId;
                                        if (matterId && matterId !== 'Unknown') {
                                          window.open(
                                            `https://app.clio.com/nc/#/matters/${matterId}/documents`,
                                            'clio-tab'
                                          );
                                        } else {
                                          showToast.error('Matter ID not available');
                                        }
                                        setActionsMenuOpen(null);
                                      }}
                                    />

                                    {/* View PDF */}
                                    {(() => {
                                      const hasPdf = invoice.pdfs?.invoice;
                                      const isValidStatus =
                                        invoice.juniperStatus
                                          ?.toLowerCase()
                                          .includes('email held') ||
                                        invoice.juniperStatus?.toLowerCase().includes('completed');
                                      const canView = hasPdf && isValidStatus;

                                      return (
                                        <ActionsMenuItem
                                          icon="HiDownload"
                                          iconColor={canView ? 'text-green-600' : 'text-gray-400'}
                                          label="View/Download Invoice"
                                          disabled={!canView}
                                          title={
                                            !canView
                                              ? `PDF not available - Transaction must be 'email-held' or 'completed' and have PDF`
                                              : ''
                                          }
                                          onClick={() => {
                                            if (canView) {
                                              handleQuickViewPdf(invoice);
                                              setActionsMenuOpen(null);
                                            }
                                          }}
                                        />
                                      );
                                    })()}

                                    {/* Download Receipts ZIP */}
                                    {(() => {
                                      const receiptCount =
                                        invoice.documents?.storedAssets?.receipts?.length || 0;
                                      return receiptCount > 0 ? (
                                        <ActionsMenuItem
                                          icon="HiDownload"
                                          iconColor="text-green-500"
                                          label={`Download ${receiptCount} Receipt${receiptCount > 1 ? 's' : ''} (ZIP)`}
                                          onClick={() => {
                                            handleDownloadReceiptsZip(invoice);
                                            setActionsMenuOpen(null);
                                          }}
                                        />
                                      ) : null;
                                    })()}

                                    <ActionsMenuDivider />

                                    {/* Send Email */}
                                    <ActionsMenuItem
                                      icon="HiMail"
                                      iconColor={
                                        invoice.juniperStatus?.toLowerCase().includes('email held')
                                          ? 'text-yellow-500'
                                          : invoice.juniperStatus
                                                ?.toLowerCase()
                                                .includes('completed')
                                            ? 'text-green-500'
                                            : 'text-gray-400'
                                      }
                                      label="Send Email"
                                      disabled={
                                        resendEmail.isPending ||
                                        invoice.emailQueued ||
                                        !isResendEligible(invoice) ||
                                        !hasValidRecipients(invoice)
                                      }
                                      title={
                                        !isResendEligible(invoice)
                                          ? 'Only available for Trust Request bills in awaiting_payment or paid status, or Email Held bills with not_required status'
                                          : !hasValidRecipients(invoice)
                                            ? 'No valid email recipients'
                                            : 'Send or resend email'
                                      }
                                      onClick={() => {
                                        openConfirmForTransaction(invoice);
                                        setActionsMenuOpen(null);
                                      }}
                                    />

                                    {/* View Bank Transactions */}
                                    <ActionsMenuItem
                                      icon="HiCreditCard"
                                      iconColor="text-xtnd-blue"
                                      label="View Bank Transactions"
                                      onClick={() => {
                                        setSelectedTransactionForBankModal(invoice);
                                        setBankTransactionsModalOpen(true);
                                        setActionsMenuOpen(null);
                                      }}
                                      disabled={!invoice.matterId || invoice.matterId === 'Unknown'}
                                      title={
                                        !invoice.matterId || invoice.matterId === 'Unknown'
                                          ? 'Matter ID not available'
                                          : 'View bank transactions for this matter'
                                      }
                                    />

                                    <ActionsMenuDivider />

                                    {/* Regenerate PDF - Grouped with refresh actions */}
                                    <ActionsMenuItem
                                      icon="HiRefresh"
                                      iconColor={
                                        isPdfRegenerationEligible(invoice)
                                          ? 'text-orange-500'
                                          : 'text-gray-400'
                                      }
                                      label="Regenerate PDF"
                                      disabled={
                                        regeneratePdf.isPending ||
                                        !isPdfRegenerationEligible(invoice)
                                      }
                                      title={
                                        !isPdfRegenerationEligible(invoice)
                                          ? 'Only available for Trust Request bills in awaiting_payment, paid, or not_required status'
                                          : 'Generate fresh PDF with new Clio Payment link'
                                      }
                                      onClick={async () => {
                                        if (isPdfRegenerationEligible(invoice)) {
                                          setActionsMenuOpen(null);
                                          openRegenerateConfirm('single', invoice);
                                        }
                                      }}
                                    />

                                    {/* Refresh from Clio */}
                                    <ActionsMenuItem
                                      icon="HiRefresh"
                                      iconColor="text-xtnd-blue"
                                      label="Refresh from Clio"
                                      disabled={reprocessing.has(invoice.id)}
                                      onClick={async () => {
                                        const id = invoice.id;
                                        setActionsMenuOpen(null);
                                        setReprocessing(prev => new Set(prev).add(id));
                                        try {
                                          showToast.success('Refreshing transaction data...');
                                          const requestId = `refresh-${id}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
                                          await api.post(`/juniper/transactions/${id}/reprocess`, {
                                            requestId,
                                            requestType: 'refresh',
                                            reason: 'Manual refresh from juniper transactions page',
                                          });
                                          showToast.success(
                                            'Refresh initiated - check back in a few moments...'
                                          );
                                        } catch (e: any) {
                                          showToast.error(
                                            `Refresh failed: ${e?.message || 'Unknown error'}`
                                          );
                                        } finally {
                                          setReprocessing(prev => {
                                            const next = new Set(prev);
                                            next.delete(id);
                                            return next;
                                          });
                                        }
                                      }}
                                    />
                                  </ActionsMenu>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Pagination */}
        {paginationComponent}

        {/* Webhook Simulation Modal */}
        <WebhookSimulationModal
          isOpen={isWebhookModalOpen}
          onClose={() => setIsWebhookModalOpen(false)}
          onSuccess={() => {
            // Optionally refresh data after successful webhook simulation
          }}
        />

        {/* Bulk Simulate Modal */}
        <BulkSimulateModal
          isOpen={bulkSimulateModalOpen}
          onClose={() => setBulkSimulateModalOpen(false)}
          onSubmit={handleBulkSimulate}
          selectedCount={
            (groupedInvoices?.flatMap(group => group.transactions) || []).filter(inv =>
              selectedTransactions.has(inv.id)
            ).length
          }
          isLoading={bulkSimulate.isPending}
        />

        {/* Bank Transactions Modal */}
        <BankTransactionsModal
          isOpen={bankTransactionsModalOpen}
          onClose={() => {
            setBankTransactionsModalOpen(false);
            setSelectedTransactionForBankModal(null);
          }}
          matterId={selectedTransactionForBankModal?.matterId || null}
          matterName={selectedTransactionForBankModal?.matterName}
        />

        {/* Download Menu */}
        <DownloadMenu
          transaction={downloadMenuState.transaction}
          isOpen={downloadMenuState.isOpen}
          onClose={() => setDownloadMenuState(prev => ({ ...prev, isOpen: false }))}
          onViewPdf={handleViewPdf}
          onViewReceipt={handleViewReceipt}
          onDownloadReceiptsZip={() => handleDownloadReceiptsZip(downloadMenuState.transaction)}
          position={downloadMenuState.position}
        />

      {/* Download Menu */}
      </>
      ) : (
        <TrustRequestsTransactionsView />
      )}

      {/* Shared Modals (shown for both tabs) */}
      <DownloadMenu
        transaction={downloadMenuState.transaction}
        isOpen={downloadMenuState.isOpen}
        onClose={() => setDownloadMenuState(prev => ({ ...prev, isOpen: false }))}
        onViewPdf={handleViewPdf}
        onViewReceipt={handleViewReceipt}
        onDownloadReceiptsZip={() => handleDownloadReceiptsZip(downloadMenuState.transaction)}
        position={downloadMenuState.position}
      />

      {/* Email Resend Confirmation Modal */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogHeader>Confirm Email Resend</DialogHeader>
        <DialogBody>
          <div>
                  <div className="font-medium">
                    {overrideActive ? 'Email will be sent to (Override):' : 'To:'}
                  </div>
                  {toRecipients.length > 0 ? (
                    <ul className="list-disc list-inside">
                      {toRecipients.map(e => (
                        <li
                          key={e}
                          className={overrideActive ? 'font-semibold text-xtnd-blue' : ''}
                        >
                          {e}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-gray-500">No recipient found</div>
                  )}
                </div>

                {overrideActive && actualRecipients.length > 0 && (
                  <div>
                    <div className="font-medium text-gray-600">
                      Original Client Recipients (for reference):
                    </div>
                    <ul className="list-disc list-inside text-gray-600">
                      {actualRecipients.map(e => (
                        <li key={e} className="line-through">
                          {e}
                        </li>
                      ))}
                    </ul>
                    <div className="text-xs text-gray-500 mt-1">
                      ↑ These recipients will NOT receive the email due to override setting
                    </div>
                  </div>
                )}
                {bccRecipients.length > 0 && (
                  <div>
                    <div className="font-medium">BCC:</div>
                    <ul className="list-disc list-inside">
                      {bccRecipients.map(e => (
                        <li key={e}>{e}</li>
                      ))}
                    </ul>
                  </div>
                )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={resendEmail.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (confirmTarget?.id) {
                  resendEmail.mutate({
                    juniperId: confirmTarget.id,
                    tenantId: confirmTarget.tenantId || 'current',
                  });
                }
                setConfirmOpen(false);
              }}
              disabled={resendEmail.isPending || confirmLoading || toRecipients.length === 0}
            >
              Send Email
            </Button>
          </DialogFooter>
        </Dialog>

        {/* PDF Regeneration Confirmation Modal */}
        <Dialog open={regenerateConfirmOpen} onClose={() => setRegenerateConfirmOpen(false)}>
          <DialogHeader>Confirm PDF Regeneration</DialogHeader>
          <DialogBody>
            <div className="space-y-3 text-sm text-xtnd-dark">
              {regenerateConfirmType === 'single' && regenerateConfirmTarget ? (
                <div>
                  <p className="font-medium">
                    Are you sure you want to regenerate the PDF for this transaction?
                  </p>
                  <div className="mt-3 bg-xtnd-light-400 rounded p-3 space-y-2">
                    <div>
                      <strong>Client:</strong> {regenerateConfirmTarget.client}
                    </div>
                    <div>
                      <strong>Matter:</strong> {regenerateConfirmTarget.matterName}
                    </div>
                    <div>
                      <strong>Amount:</strong>{' '}
                      {numberUtil.formatCurrency(
                        regenerateConfirmTarget.trustRequest?.total ||
                          regenerateConfirmTarget.trustRequest?.amount ||
                          0
                      )}
                    </div>
                    <div>
                      <strong>Status:</strong>{' '}
                      {formatRawStatus(regenerateConfirmTarget.trustRequest?.state)}
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-gray-600">
                    This will generate a fresh PDF with a new Clio Payment link. The existing PDF
                    will be replaced.
                  </p>
                </div>
              ) : regenerateConfirmType === 'bulk' && regenerateConfirmBulkIds.length > 0 ? (
                <div>
                  <p className="font-medium">
                    Are you sure you want to regenerate PDFs for {regenerateConfirmBulkIds.length}{' '}
                    transaction{regenerateConfirmBulkIds.length > 1 ? 's' : ''}?
                  </p>
                  <div className="mt-3 bg-xtnd-light-400 rounded p-3">
                    <p className="text-xs text-gray-600">
                      This will generate fresh PDFs with new Clio Payment links for all eligible
                      transactions. Only Trust Request bills in "awaiting_payment", "paid", or
                      "not_required" status will be regenerated.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRegenerateConfirmOpen(false)}
              disabled={regeneratePdf.isPending || bulkRegeneratePdf.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleRegenerateConfirm}
              disabled={regeneratePdf.isPending || bulkRegeneratePdf.isPending}
              loading={regeneratePdf.isPending || bulkRegeneratePdf.isPending}
            >
              {regeneratePdf.isPending || bulkRegeneratePdf.isPending
                ? 'Regenerating...'
                : 'Regenerate PDF'}
            </Button>
          </DialogFooter>
        </Dialog>
    </PageLayout>
  );
}
